import cx from "classnames";
import { isEqual } from "lodash";
import * as React from "react";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { getScrollBarWidth } from "@app/shared/utility/getScrollBarWidth";
import { ColumnFilter, wafflesDefaultFiltering, WafflesFilterGroup } from "@app/shared/waffles/filterExports";
import WafflesFilterSystem from "@app/shared/waffles/WafflesFilterSystem";
import WafflesPagination from "@app/shared/waffles/WafflesPagination";
import { JSX } from "react";
import { getClipPath } from "@app/shared/waffles/clipPathHelpers";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesColors } from "@app/shared/qes-standards/colors"
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { WafflesThemes, WafflesExpansionToggleStatus, WafflesClassNames, ColumnGroup, ColumnConfig, SortDirections, TextAlignment, Virtualization, WafflesColumn, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles/exports";

interface WaffleProps<T> {
  // Required
  /** The data to be rendered. It will be rendered in the order it is declared, while excluding null cells.<br />
   * Columns in which every row is null are left out of the table completely.<br />
   * The only item that won't be rendered is "key," which is used for sorting and tracking selections (mostly in case the defining column is a function/node instead of text)<br />
   * Columns in which at least one row has a value are filtered into a Used Columns variable.<br />
   * The data object *requires* a key property of type string.
   */
  data: T[];

  /**
   * Required string ID for the table. Should be consistent on every render, since it's used by QA for automated testing.
   */
  testId: string;

  // All Optional
  /**
   * This will be rendered as-is in its own \<tr> above the rest of the body.<br />
   * Anything in the bar reliant on data/selections depends on the parent.<br />
   */
  bulkActionBar?: React.ReactNode;

  /**
   * Overrides the bulk checkbox checked state.<br />
   */
  bulkCheckboxCheckedOverride?: boolean;

  /**
   * Overrides the bulk checkbox indeterminate state.<br />
   */
  bulkCheckboxIndeterminateOverride?: boolean;

  /**
   * Column Groups are rendered as a first \<tr> of \<th>s inside \<thead>.<br />
   * They will not render if the sum of their colSpans is not equal to the number of Used Columns.
   */
  columnGroups?: Array<ColumnGroup<T>>;
  /**
   * This prop allows the author to set various properties of each individual column.<br />
   * Being listed in this prop does not mean a column is rendered, so it's ideal to list every possible column here.
   */
  columns?: ColumnConfig;
  /**
   * Compare Columns are the ones that only render a single row in compare mode (e.g. the key).<br />
   * Will default to 1 if compareData is given and compareModeKeyColumns is not.
   */
  compareModeKeyColumns?: number;
  /**
   * The data used to compare.
   */
  compareData?: T[];
  /**
   * Expansions for compared data. To be properly interleaved with dataExpansions, the keys of the two expansions must match.
   */
  compareExpansions?: Map<string, T[]>;

  /**
   * Rowgroups for compared data. If you have rowGroups and compareData,
   * this is necessary to help compared rows end up in the right category instead of being appended to every category.
   */
  compareRowGroups?: Map<string, string[]>;

  /**
   * Waffles' built-in sorting works fine if every value passed in is a string or a number.<br />
   * Unfortunately, that isn't often the case. This function needs to handle a case for every column in the data, not just the "special" cases.<br />
   * It returns a new dataset.<br /><br />

   * Waffles does not maintain any record of which rows are checked, instead relying entirely on the selectedRecords prop.<br />
   * This function passes the complete list of rows that are selected to the parent, so it can handle tracking that.<br />
   * Be sure it updates the selectedRecords prop.
   */
  customSort?: (segment: T[], column: keyof T, direction: SortDirections) => T[];

  /**
   * A map of string keys (row ids) to an array of T[]. This is used when a row has children, e.g. a specialty group has other specialties to enumerate.<br />
   */
  dataExpansions?: Map<string, T[]>;

  /**
   * Boolean to render dataExpansions as open by default
   */
  dataExpansionsDefaultOpen?: boolean;

  /**
   * Boolean to give the children in a data expansion each their own checkbox
   */
  dataExpansionsHaveIndividualCheckboxes?: boolean;

  /**
   * Event handler for clicking the data expansion toggle icon. This should update the parent's data to be passed back in through dataExpansions.
   */
  dataExpansionOnToggleClick?: (expansionKey: string, isCompared: boolean) => Promise<void>;

  /**
   * This is intended to be used for compare mode when data and compare data have different/mismatched rows.
   */
  defaultDisplayOrder?: Map<string, number>;

  /**
   * Disables the "loading" view with the spinner and transparent white overlay
   * While this view is generally desirable for UX when the table is requesting new data,
   * it also is annoying when the only change was a user clicking a checkbox
   */
  disableProcessingOverlay?: boolean;

  /**
   * Determines whether the header sticks to the top of the screen when the page has scrolled past the top of the table
   */
  disableStickyHeader?: boolean;

  /**
   * The string to show when there is no data or no used columns. Defaults to "There are no records for this selection."
   */
  emptyTableMessage?: string;

  /**
   * A boolean to enable pagination for the table. This should be used in conjunction with paginationProps.<br />
   * This will override loadMore.
   */
  enablePagination?: boolean;

  /**
   * Sets the table container's minimum width to 100%
   */
  fullWidth?: boolean;

  /**
   * A function called every time a checkbox is clicked. <br />
   * Used in conjunction with showCheckboxes.
   * @returns void
   */
  handleCheckboxChange?: (selectedKeys: string[], isBulk?: boolean) => void;

  /**
   * Visually hides the header bulk checkbox.
   */
  hideBulkCheckbox?: boolean;

  /**
   * Visually hides the table header
   */
  hideTableHead?: boolean;

  /**
   * A list of indeterminate keys to show indeterminate checkboxes. This is optional, and takes precedence over selectedKeys.
   */
  indeterminateKeys?: string[];

  /* infiniteScroll works together with loadMore. It replaces the actual Load More button and instead automatically fires the loadMore function at a certain threshold.
   */
  infiniteScroll?: boolean;

  /**
   * In some cases, it's a helpful a11y feature to use \<th> instead of \<td> for a cell that describes a row (along with scope="row"). <br />
   * This prop declares which columns get that treatment
   */
  keyColumns?: string[];

  /**
   * Indicates whether the parent component is still loading its data.
   */
  loading?: boolean;

  /**
   * This function is intended to fetch more data and the parent component needs to update the data prop with it. <br />
   * It returns a boolean to determine whether the Load More button should continue to be rendered or whether the scroll event handler
   * on the window can be safely removed.<br />
   * Load more does not work with standard waffles customSort. customSort sorts only the visible records.<br />
   * To correctly sort all records, passThroughSorting must be used (see that prop for more details)
   */
  loadMore?: (justChecking: boolean) => boolean;

  /**
   * Overrides the default "Load More" button text. For example, the job-performance tables
   * pass "See More" here instead of the default "Load More".
   */
  loadMoreLabel?: string;

  /**
   * This function handles filtering. I'd like to do this internally, but it's just not possible for Waffles to
   * handle all the typing for a generic's children.<br />
   * Recommended for use with virtualization.
   */
  onFilterChange?: (row: T, columnFilter: ColumnFilter) => boolean;

  /**
   * This function handles the user clicking a row. Not called when the row is disabled.
   * Ignores clicks on button, input, anchor, elements or any element with role='button' within the row.
   */
  onRowClick?: (row: T) => void;

  /**
   * This function handles the user's mouse entering and leaving a row
   */
  onRowMouseOver?: (row: T) => void;

  /**
   * The props required for the pagination bar. This needs to be used in conjunction with enablePagination.<br />
   * WafflesPaginationProps includes:<br />
   * changePage (pageNumber: number) => void<br />
   * [optional, unused] changeRecordsPerPage (pageNumber: number, recordsPerPage: number) => void<br />
   * [optional] classNames (string)<br />
   * recordsPerPage (number)<br />
   * skip (number)<br />
   * totalRecords (number)<br /><br />
   * The page number is calculated internally based on the skip and recordsPerPage props, and is 0-indexed internally but 1-indexed in the UI.
   * The totalRecords value is only used in pagination, and not for the bulk checkbox. The totalNumberOfRecords prop should also be used.
   */
  paginationProps?: WafflesPaginationProps;

  /**
   * Tells Waffles to not handle the filtering, but instead just pass the filters to the function provided.<br />
   * This is useful for when there's more data than Waffles has at a given time, e.g. there's a load more button.
   */
  passThroughFiltering?: (filters: WafflesFilterGroup[], sortedColumn?: keyof T, sortDirection?: SortDirections) => { data: T[], comparedData: T[] };

  /**
   * Tells Waffles to not handle the sorting at all, but just listen for new props.
   * This is useful for when there's more data than Waffles has at a given time, e.g. there's a load more button.
   */
  passThroughSorting?: boolean;

  /**
   * When true, the first click on a column header sorts descending instead of ascending.
   */
  invertInitialSort?: boolean;

  /**
   * Overrides the sort direction.<br />
   */
  sortDirectionOverride?: SortDirections;

  /**
   * Overrides the sorted column.<br />
   */
  sortedColumnOverride?: keyof T;

  /**
   * Tells Waffles that we're in compare mode even though compareData is undefined/empty
   */
  preSortedCompareMode?: boolean;

  /**
 * Tells Waffles to only show checkboxes on expansion rows, and hide them on the parent rows.
 */
  showExpansionCheckboxesOnly?: boolean;

  /**
   * Custom renderer for expansion panel content. When provided, expansion rows are rendered
   * as a single full-width panel spanning all columns instead of individual column cells.
   */
  renderExpansionPanel?: (expansionKey: string) => React.ReactNode;

  /**
   * Function called to retrieve class list for each row
   */
  rowDecorator?: (row: T, isCompareRow: boolean) => string;

  /**
   * Used to give categories for rows
   * First Map parameter is the category, second is the array of record keys
   */
  rowGroups?: Map<string, string[]>;

  /**
   * A list of the selected records/checked boxes. Required for use with checkboxes.
   */
  selectedKeys?: string[];

  /**
   * Determines whether the table shows checkboxes.
   */
  showCheckboxes?: boolean;

  /**
   * Used to change the overall appearance of the table.<br />
   * Theme scss files have the format _waffles-theme-${theme name}.scss.
   */
  theme?: WafflesThemes;

  /**
   * The total number of records from the parent component. Useful for when there's a filter.
   * Specifically, when a filter is in place, we need to know the number of records
   * so we know how to handle the bulk checkbox's state.
   * Also useful when there are data expansions, as Waffles doesn't necessarily count the expansion rows as rows.
   */
  totalNumberOfRecords?: number;

  /**
   * Implements vertical headers, which invert the position of columns and column groups, in addition to rotating the columns 180 degrees.
   */
  useVerticalHeaders?: boolean;

  /**
   * Enables windowing/virtualization, to only render a limited number of rows instead of the entire data set.<br />
   * This prop was implemented in 2020 and has never been used in production, and has a number of bugs interacting with other props (data expansions, disable sticky header, rowGroups, vertical headers).<br />
   */
  virtualize?: Virtualization;

  /**
   * Opt-in visual variant for the table.<br />
   * "frozen-border": renders a gradient shadow on the right edge of the frozen column zone when sticky columns are present.
   */
  variant?: "frozen-border";

}

interface WaffleState<T> {
  columnWidths: Map<keyof T, string>;
  compareMode: boolean;
  containerScrollLeft: number;
  containerWidth: number;
  expansionData: Map<string, T[]>;
  expansionOpenStatusMap: Map<string, WafflesExpansionToggleStatus>;
  fakeTableHead: JSX.Element;
  records: Map<string, T[]>;
  scrollTop: number;
  showLoadMore: boolean;
  sortedColumn: keyof T;
  sortDirection: SortDirections;
  tableRows: JSX.Element[];
  tableHead: JSX.Element;
  usedColumns: Array<keyof T>;
}

/**
 * The catch-all QES table component. The only required props are data and testId, but you'll almost always want columns as well.<br />
 * Its other options include compare mode, expanding rows (data expansions), row categories (row groups), checkboxes per row, multiple ways to sort (internal/default, customSort, or passThroughSorting), pagination, load more functionality, infinite scrolling, and more.<br />
 * <br/>
 * Docs note: The loading spinner won't appear because its styling depends entirely on Bootstrap/Looper.<br />
 * Docs note: The mock header is there for the "Default" view, to show how it looks in QES. The sticky header leaves that 58px of space at the top because of the top bar in the app.<br />
 */
class Waffles<T extends { [wafflesKeyColumnName]: string, isExpansion?: boolean, rowIsDisabled?: boolean, parentKey?: string, checkBoxIsDisabled?: boolean }> extends React.Component<WaffleProps<T>, WaffleState<T>> {
  private arbitraryMinimumColumnWidth = 15;
  private filters: WafflesFilterGroup[];
  private hasResizedColumns = false;
  private headerExtraSpace: number;
  private includeFiltering = false;
  private initialRender = true;
  private loadMoreButtonRef: React.RefObject<HTMLButtonElement>;
  private numberOfRecords: number = null;
  private processingOverlayRef: React.RefObject<HTMLDivElement>;
  private readonly scrollBarThickness = getScrollBarWidth();
  private scrollInterval: number = null; // Used to animate scrolling left/right, and to ensure the scrolling stops when the mouse moves/there's no further to go.
  private scrollLeftRef: React.RefObject<HTMLDivElement>;
  private scrollLeftOverlayRef: React.RefObject<HTMLDivElement>;
  private scrollRightRef: React.RefObject<HTMLDivElement>;
  private scrollRightOverlayRef: React.RefObject<HTMLDivElement>;
  private tableContainerRef: React.RefObject<HTMLDivElement>;
  private tableHeadHeight = 0;
  private tableScrollTimeout: number = null;
  private wafflesResizePositionIndicatorRef: React.RefObject<HTMLDivElement>;
  private windowResizeInterval: number = null; // Used to debounce window resizing
  private wafflesScrollableIndicatorHeight = "0px";
  private windowScrollInterval: number = null; // Used to debounce window scrolling

  constructor(props: WaffleProps<T>) {
    super(props);

    this.includeFiltering = !!(this.props.passThroughFiltering || this.props.onFilterChange);
    this.loadMoreButtonRef = React.createRef<HTMLButtonElement>();
    this.processingOverlayRef = React.createRef<HTMLDivElement>();
    this.wafflesResizePositionIndicatorRef = React.createRef<HTMLDivElement>();
    this.scrollLeftRef = React.createRef<HTMLDivElement>();
    this.scrollRightRef = React.createRef();
    this.scrollLeftOverlayRef = React.createRef<HTMLDivElement>();
    this.scrollRightOverlayRef = React.createRef();
    this.tableContainerRef = React.createRef();

    const newExpansionData = this.buildExpansionData(this.props.compareData?.length > 0 || this.props.preSortedCompareMode);
    const newExpansionStatusMap = new Map<string, WafflesExpansionToggleStatus>([...newExpansionData.keys()].map((key) =>
      ([key, !!this.props.dataExpansionsDefaultOpen ? WafflesExpansionToggleStatus.Open : WafflesExpansionToggleStatus.Closed])));

    this.state = {
      columnWidths: new Map<keyof T, string>(),
      compareMode: this.props.compareData?.length > 0 || this.props.preSortedCompareMode,
      containerScrollLeft: 0,
      containerWidth: null,
      expansionData: newExpansionData,
      expansionOpenStatusMap: newExpansionStatusMap,
      fakeTableHead: null,
      records: this.buildRecords(),
      scrollTop: 0,
      showLoadMore: !!this.props.loadMore && !this.props.enablePagination,
      sortDirection: this.props.sortDirectionOverride ?? SortDirections.None,
      sortedColumn: this.props.sortedColumnOverride ?? null,
      tableHead: null,
      tableRows: null,
      usedColumns: [],
    };

    this.filters = [];
  }

  public componentDidMount(): void {
    window.requestAnimationFrame(() => {
      const updatedUsedColumns = this.getUsedColumns();
      const headers = this.buildHeaders(updatedUsedColumns);
      const newRecords = this.buildRecords();
      const body = this.buildTBody(updatedUsedColumns, newRecords);
      const newColumnWidths = this.recordColumnWidths(updatedUsedColumns);
      this.initialRender = false;
      this.setState({
        columnWidths: newColumnWidths,
        fakeTableHead: headers.fakeHeader,
        records: newRecords,
        showLoadMore: !!this.props.loadMore && this.props.loadMore(true) && !this.props.enablePagination,
        tableHead: headers.thead,
        tableRows: body,
        usedColumns: updatedUsedColumns,
      }, () => {
        if (!this.props.passThroughSorting) {
          this.props.customSort ? this.processChanges(() => this.handleCustomSort(this.state.sortedColumn, this.state.sortDirection)) : this.processChanges(() => this.handleInternalSort(this.state.sortedColumn, true));
        }
        this.numberOfRecords = this.state.compareMode ? [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0) / 2 : [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0);
      });
    });

    this.numberOfRecords = this.state.compareMode ? [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0) / 2 : [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0);
  }

  public componentDidUpdate(prevProps: WaffleProps<T>, prevState: WaffleState<T>): void {
    let newState = {} as Partial<WaffleState<T>>;
    const callbacks = new Array<() => void>();
    // We've rendered but haven't recorded column sizes yet
    if (this.state.columnWidths.size === 0 && this.tableContainerRef.current && this.state.usedColumns.length > 0 && this.props.data.length > 0) {
      const newColumnWidths = this.recordColumnWidths(this.state.usedColumns);
      newState = {
        ...newState,
        columnWidths: newColumnWidths,
      };
      callbacks.push(() => {
        this.initialRender = false; // now that we have columnWidths
        this.setEventListeners();
        const updatedHeaders = this.buildHeaders(this.state.usedColumns);
        const updatedTable = this.buildTBody(this.state.usedColumns, this.state.records);
        this.setState({
          fakeTableHead: updatedHeaders.fakeHeader,
          tableHead: updatedHeaders.thead,
          tableRows: updatedTable,
        });
      });
    }

    // Data changed or compareData changed
    if (
      this.tableContainerRef.current
      && (
        (!isEqual(prevProps.dataExpansions, this.props.dataExpansions) || (!isEqual(prevProps.columns, this.props.columns))) ||
        (!isEqual(prevProps.data, this.props.data) || !isEqual(prevProps.compareData, this.props.compareData))
      )
    ) {
      const newUsedColumns = this.getUsedColumns();
      const headers = this.buildHeaders(newUsedColumns);
      const newRecords = this.buildRecords();
      const isCompareMode = this.props.compareData?.length > 0 || this.props.preSortedCompareMode;
      const newExpansionData = this.buildExpansionData(isCompareMode);
      const newExpansionStatusMap = new Map<string, WafflesExpansionToggleStatus>([...newExpansionData.keys()].map((key) => {
        const newStatus =
          newExpansionData.get(key) !== undefined && newExpansionData.get(key).length > 0 && this.state.expansionOpenStatusMap.has(key) ?
            this.state.expansionOpenStatusMap.get(key) :
            !!this.props.dataExpansionsDefaultOpen ?
              WafflesExpansionToggleStatus.Open :
              WafflesExpansionToggleStatus.Closed;
        return [key, newStatus];
      }));

      newState = {
        ...newState,
        columnWidths: this.recordColumnWidths(newUsedColumns),
        compareMode: isCompareMode,
        expansionData: newExpansionData,
        expansionOpenStatusMap: newExpansionStatusMap,
        fakeTableHead: headers.fakeHeader,
        records: newRecords,
        showLoadMore: !!this.props.loadMore && this.props.loadMore(true) && !this.props.enablePagination,
        tableHead: headers.thead,
        tableRows: this.buildTBody(newUsedColumns, newRecords),
        usedColumns: newUsedColumns,
      };

      callbacks.push(() => {
        if (!this.props.passThroughSorting) {
          this.props.customSort ? this.processChanges(() => this.handleCustomSort(this.state.sortedColumn, this.state.sortDirection)) : this.processChanges(() => this.handleInternalSort(this.state.sortedColumn, true));
        }

        if (this.props.dataExpansionsHaveIndividualCheckboxes) {
          let numberOfRecords = 0;
          const expansionDataKeys = Array.from(this.state.expansionData.keys());
          for (const x of this.props.data) {
            if (!expansionDataKeys.includes(x[wafflesKeyColumnName])) {
              numberOfRecords = numberOfRecords + 1;
            }
          }

          for (const [, value] of this.state.expansionData) {
            numberOfRecords = numberOfRecords + value.length;
          }

          this.numberOfRecords = numberOfRecords;
        } else {
          this.numberOfRecords = this.state.compareMode ? [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0) / 2 : [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0);
        }

        this.setState({
          columnWidths: this.recordColumnWidths(newUsedColumns),
        });
      });
    }

    if (this.tableContainerRef.current && !isEqual(prevState.expansionData, this.state.expansionData)) {
      const newRecords = this.buildRecords();
      newState = {
        ...newState,
        records: newRecords,
        tableRows: this.buildTBody(this.state.usedColumns, newRecords),
      }

      callbacks.push(() => {
        if (!this.props.passThroughSorting) {
          this.props.customSort ? this.processChanges(() => this.handleCustomSort(this.state.sortedColumn, this.state.sortDirection)) : this.processChanges(() => this.handleInternalSort(this.state.sortedColumn, true));
        }

        if (this.props.dataExpansionsHaveIndividualCheckboxes) {
          let numberOfRecords = 0;
          const expansionDataKeys = Array.from(this.state.expansionData.keys());
          for (const x of this.props.data) {
            if (!expansionDataKeys.includes(x[wafflesKeyColumnName])) {
              numberOfRecords = numberOfRecords + 1;
            }
          }

          for (const [, value] of this.state.expansionData) {
            numberOfRecords = numberOfRecords + value.length;
          }

          this.numberOfRecords = numberOfRecords;
        } else {
          this.numberOfRecords = this.state.compareMode ? [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0) / 2 : [...this.state.records.values()].reduce((acc, val) => acc += val.length, 0);
        }
      })
    }

    //// Data changed and there wasn't data before
    //if (!this.tableContainerRef.current && this.state.usedColumns.length === 0 && !isEqual(prevProps.data, this.props.data)) {
    //  const newUsedColumns = this.getUsedColumns();
    //  newState = {
    //    ...newState,
    //    usedColumns: newUsedColumns,
    //  };
    //}

    // The table container width changed
    if (this.tableContainerRef.current && this.tableContainerRef.current.clientWidth !== this.state.containerWidth) {
      // making sure the sticky header in particular obeys the new width
      const newUsedColumns = this.getUsedColumns();
      const updatedHeaders = this.buildHeaders(newUsedColumns, this.state.sortedColumn, this.state.sortDirection, this.tableContainerRef.current.clientWidth);
      newState = {
        ...newState,
        columnWidths: this.recordColumnWidths(newUsedColumns),
        containerWidth: this.tableContainerRef.current.clientWidth,
        fakeTableHead: updatedHeaders.fakeHeader,
        tableHead: updatedHeaders.thead,
        usedColumns: newUsedColumns,
      };
    }

    // If either the columnWidths, selectedKeys, indeterminateKeys or showCheckboxes (from the parent) changed, we want to rerender the component
    if (!isEqual(prevState.columnWidths, this.state.columnWidths) ||
      !isEqual(prevProps.selectedKeys, this.props.selectedKeys) ||
      !isEqual(prevProps.indeterminateKeys, this.props.indeterminateKeys) ||
      !isEqual(prevProps.showCheckboxes, this.props.showCheckboxes)) {
      const newUsedColumns = this.getUsedColumns();
      const updatedHeaders = this.buildHeaders(newUsedColumns);
      const updatedTable = this.buildTBody(newUsedColumns, this.state.records);
      newState = {
        ...newState,
        fakeTableHead: updatedHeaders.fakeHeader,
        tableHead: updatedHeaders.thead,
        tableRows: updatedTable,
        usedColumns: newUsedColumns,
      };
    }

    // expansion data has updated, selected or compared
    if (!isEqual(prevProps.dataExpansions, this.props.dataExpansions) ||
      !isEqual(prevProps.compareExpansions, this.props.compareExpansions)) {
      const newExpansionData = this.buildExpansionData((this.props.compareData?.length > 0) || this.props.preSortedCompareMode);

      // this change will get picked up by an earlier conditional on the next update,
      // and that'll handle updating the expansionOpenStatusMap and the tableRows
      newState = {
        ...newState,
        expansionData: newExpansionData,
      };
    }

    // We use getBoundingClientRect.width for widths instead of clientWidth, because the former is accurate and the latter rounds to the nearest pixel, potentially leaving a tiny bit of space
    if (this.tableContainerRef.current && this.props.columns &&
      Object.values(this.props.columns).some((col) => col.sticky) &&
      ((!isEqual(this.state.columnWidths, prevState.columnWidths)) ||
        !isEqual(prevState.records, this.state.records) ||
        (this.props.virtualize && (prevState.scrollTop !== this.state.scrollTop)))) {
      // as above, requestAnimationFrame forces React to wait until the latest DOM updates are actually rendered on the page
      // Otherwise, this was having issues with skipping rows
      // But in this case we don't care if the component unmounts first because there's no state update
      const leftOffsetMap: Map<number, number> = new Map<number, number>();
      let useleftOffsetMap = false;

      // save the left offset for sticky columns on horizontal scroll
      // based on the first non-bulk-action row,
      // as it will always have the correct offset values due to it always being visible
      window.requestAnimationFrame(() => {
        if (this.tableContainerRef.current) {
          Array.from(this.tableContainerRef.current.querySelectorAll(`.${WafflesClassNames.tr}`)).forEach((row) => {
            let stickLeft = 0;

            Array.from(row.querySelectorAll(".sticky")).forEach((stickyCell, stickyCellIndex) => {
              if (useleftOffsetMap) {
                const storedLeftOffsetValue = leftOffsetMap.get(stickyCellIndex);
                (stickyCell as HTMLElement).style.left = `${storedLeftOffsetValue}px`;
              } else {
                if (!row.classList.contains("bulk-action") && !row.classList.contains("category")) {
                  leftOffsetMap.set(stickyCellIndex, stickLeft);
                  (stickyCell as HTMLElement).style.left = `${stickLeft}px`;
                  stickLeft += Math.floor(stickyCell.getBoundingClientRect().width);
                }
              }
            });

            if (!row.classList.contains("bulk-action") && !row.classList.contains("category")) {
              useleftOffsetMap = true;
            }
          });

          Array.from(this.tableContainerRef.current.querySelectorAll(".waffles-head-row")).forEach((row) => {
            let stickLeft = 0;
            Array.from(row.querySelectorAll(".sticky")).forEach((stickyCell) => {
              (stickyCell as HTMLElement).style.left = `${stickLeft}px`;
              stickLeft += Math.floor(stickyCell.getBoundingClientRect().width);
            });
          });
        }
      });
    }

    if (prevProps.loadMore !== this.props.loadMore) {
      newState = {
        ...newState,
        showLoadMore: !!this.props.loadMore && this.props.loadMore(true) && !this.props.enablePagination,
      };
    }

    if (prevState.expansionOpenStatusMap !== this.state.expansionOpenStatusMap) {
      const newBody = this.buildTBody(newState.usedColumns || this.state.usedColumns, newState.records || this.state.records);
      const newColumnWidths = this.recordColumnWidths(newState.usedColumns || this.state.usedColumns);
      const newHeaders = this.buildHeaders(newState.usedColumns || this.state.usedColumns, newState.sortedColumn || this.state.sortedColumn, newState.sortDirection || this.state.sortDirection, this.state.containerWidth,);

      newState = {
        ...newState,
        columnWidths: newColumnWidths,
        tableHead: newHeaders.thead,
        tableRows: newBody,
      };

      callbacks.push(() => {
        const newHeaders = this.buildHeaders(this.state.usedColumns, this.state.sortedColumn, this.state.sortDirection, this.state.containerWidth);
        this.setState({
          fakeTableHead: newHeaders.fakeHeader,
        });
      })
    }

    if (prevProps.bulkActionBar !== this.props.bulkActionBar) {
      // In the case of this being implemented, the "Apply CMS Credit" toggle was put into bulk action,
      // and that toggle can show or hide particular columns. So everything needs to be rebuilt.
      const newUsedColumns = this.getUsedColumns();
      const newHeaders = this.buildHeaders(newUsedColumns);
      const newBody = this.buildTBody(newUsedColumns, newState.records || this.state.records);
      newState = {
        ...newState,
        fakeTableHead: newHeaders.fakeHeader,
        tableHead: newHeaders.thead,
        tableRows: newBody,
      };
    }

    if (prevProps.useVerticalHeaders !== this.props.useVerticalHeaders ||
      prevProps.columnGroups !== this.props.columnGroups) {
      const newHeaders = this.buildHeaders(newState.usedColumns || this.state.usedColumns);
      newState = {
        ...newState,
        fakeTableHead: newHeaders.fakeHeader,
        tableHead: newHeaders.thead,
      }
    }

    if (prevProps.compareModeKeyColumns !== this.props.compareModeKeyColumns ||
      prevProps.dataExpansionsHaveIndividualCheckboxes !== this.props.dataExpansionsHaveIndividualCheckboxes) {
      const newHeaders = this.buildHeaders(newState.usedColumns || this.state.usedColumns);
      const newBody = this.buildTBody(newState.usedColumns || this.state.usedColumns, newState.records || this.state.records);
      newState = {
        ...newState,
        fakeTableHead: newHeaders.fakeHeader,
        tableHead: newHeaders.thead,
        tableRows: newBody,
      }
    }

    if (prevProps.rowDecorator !== this.props.rowDecorator) {
      const newBody = this.buildTBody(newState.usedColumns || this.state.usedColumns, newState.records || this.state.records);
      newState = {
        ...newState,
        tableRows: newBody,
      };
    }

    if (prevProps.rowGroups !== this.props.rowGroups ||
      prevProps.defaultDisplayOrder !== this.props.defaultDisplayOrder) {
      const newRecords = this.buildRecords();
      const newBody = this.buildTBody(newState.usedColumns || this.state.usedColumns, newRecords);
      newState = {
        ...newState,
        records: newRecords,
        tableRows: newBody,
      }
    }

    // set all the state updates and call their callbacks
    if (Object.keys(newState).length > 0) {
      this.setState((oldState) => ({
        ...oldState,
        ...newState,
      }), () => {
        callbacks.forEach((callback) => callback());
      });
    }

    // this updates the horizontal scroll indicator height upon filtering
    if (!isEqual(prevState.tableRows, this.state.tableRows) && !this.props.virtualize &&
      this.scrollLeftRef.current && this.scrollRightRef.current && this.tableContainerRef.current) {
      const thead = this.tableContainerRef.current.querySelector("thead");
      const tbody = this.tableContainerRef.current.querySelector("tbody");
      const headHeight = thead ? thead.getBoundingClientRect().height : this.tableHeadHeight;
      const newHeight = !!this.props.virtualize ?
        `${Math.min(this.props.virtualize.viewportHeight - headHeight - this.scrollBarThickness, tbody && tbody.getBoundingClientRect().height)}px` :
        this.tableContainerRef.current ?
          `${Math.max(this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table}`).getBoundingClientRect().height - headHeight, this.tableContainerRef.current.clientHeight - headHeight)}px` :
          "0px";
      this.wafflesScrollableIndicatorHeight = newHeight;
      this.scrollLeftRef.current.style.height = this.wafflesScrollableIndicatorHeight;
      this.scrollRightRef.current.style.height = this.wafflesScrollableIndicatorHeight;
      if (this.scrollLeftOverlayRef.current) {
        this.scrollLeftOverlayRef.current.style.height = this.wafflesScrollableIndicatorHeight;
      }
      if (this.scrollRightOverlayRef.current) {
        this.scrollRightOverlayRef.current.style.height = this.wafflesScrollableIndicatorHeight;
      }
    }

    if (this.filters.length && !isEqual(prevState.records, this.state.records) && this.scrollLeftRef.current) {
      this.handleTableScroll();
    }

    // This can get out of sync after expansions are toggled, but not until the render after that update
    if (this.tableContainerRef.current && this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table}`) && this.tableContainerRef.current.querySelector("thead")) {
      const tableHeight = this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table}`).getBoundingClientRect().height;
      const headerHeight = this.tableContainerRef.current.querySelector("thead").getBoundingClientRect().height;
      const scrollBarHeight = this.scrollRightRef.current.getBoundingClientRect().height;
      const expectedHeight = Math.max(tableHeight - headerHeight, this.tableContainerRef.current.clientHeight - headerHeight);
      if (expectedHeight !== scrollBarHeight) {
        const thead = this.tableContainerRef.current && this.tableContainerRef.current.querySelector("thead");
        const tbody = this.tableContainerRef.current && this.tableContainerRef.current.querySelector("tbody");
        const headHeight = thead ? thead.getBoundingClientRect().height : this.tableHeadHeight;
        const newHeight = !!this.props.virtualize ?
          `${Math.min(this.props.virtualize.viewportHeight - headHeight - this.scrollBarThickness, tbody && tbody.getBoundingClientRect().height)}px` :
          `${expectedHeight}px`;
        this.scrollRightRef.current.style.height = `${newHeight}px`;
        this.scrollLeftRef.current.style.height = `${newHeight}px`;
        if (this.scrollRightOverlayRef.current) {
          this.scrollRightOverlayRef.current.style.height = `${newHeight}px`;
        }
        if (this.scrollLeftOverlayRef.current) {
          this.scrollLeftOverlayRef.current.style.height = `${newHeight}px`;
        }
      }
    }

    if (this.tableContainerRef.current && !isEqual(prevState.tableHead, this.state.tableHead) && this.tableContainerRef.current.querySelector("thead")) {
      this.tableHeadHeight = this.tableContainerRef.current.querySelector("thead").getBoundingClientRect().height;
    }
  }

  public componentWillUnmount(): void {
    // Just in case - we don't need anything firing after unmounting
    // This is especially important on window event handlers
    this.clearScrollInterval();
    this.clearResizeInterval();
    this.clearWindowScrollInterval();
    this.clearEventListeners();
  }

  public render(): JSX.Element {
    let noResults: JSX.Element;
    const noFilteredRecords = this.includeFiltering && this.filters.length > 0 && (this.state.records.size === 0 || this.state.records.values().next().value.length === 0);
    // There's data but it's been filtered out externally, like the Changed Gaps toggle
    if ((!this.props.data.length && !(this.props.compareData && this.props.compareData.length)) ||
      (noFilteredRecords) ||
      this.state.usedColumns.length === 0) {
      noResults = <h3 className="text-muted no-content-text">{this.props.emptyTableMessage !== undefined ? this.props.emptyTableMessage : "There are no records for this selection."}</h3>;
    }

    // These are all probably okay to stay in render
    const thead = this.tableContainerRef.current && this.tableContainerRef.current.querySelector("thead");
    const tbody = this.tableContainerRef.current && this.tableContainerRef.current.querySelector("tbody");
    const headHeight = thead ? thead.getBoundingClientRect().height : this.tableHeadHeight;
    const outerContainerStyle = {
      // making sure the filtering box doesn't suddenly shrink
      minWidth: this.props.fullWidth && !!noResults && this.includeFiltering ? "100%" : null,
    };
    // declare the type of THIS style block because overflowY will prevent compilation otherwise.
    // the overflow is there because the loading spinner can get very slightly taller than the container
    // while spinning, causing a useless scroll bar to appear or flicker
    const containerStyle: React.CSSProperties = {
      maxHeight: this.props.virtualize ? this.props.virtualize.viewportHeight : null,
      //minWidth: this.props.fullWidth && !this.hasResizedColumns ? "100%" : null,
      overflowY: this.props.loading ? "visible" : null,
    };
    const tableContainer = this.tableContainerRef && this.tableContainerRef.current;
    const wafflesTable = tableContainer && tableContainer.querySelector(`.${WafflesClassNames.table}`);
    const tableStyle = {
      height: this.props.virtualize && !this.initialRender && thead ? ([...this.state.records.values()].reduce((sum, item) => sum += item.length, 0) * this.props.virtualize.rowHeight) + headHeight : null,
      minWidth: this.props.fullWidth && !this.hasResizedColumns ? "100%" : null,
    };
    const scrollBarContainerStyle = {
      width: this.state.containerWidth,
    };
    let loadMoreStyles = this.state.showLoadMore && this.loadMoreButtonRef.current ? window.getComputedStyle(this.loadMoreButtonRef.current) : null;
    if (!loadMoreStyles && this.props.enablePagination && this.tableContainerRef.current) {
      const paginationBar = this.tableContainerRef.current.parentElement.querySelector(".waffles-pagination");
      loadMoreStyles = paginationBar ? window.getComputedStyle(paginationBar) : null;
    }
    const hasVerticalScroll = tableContainer && tableContainer.scrollHeight > tableContainer.clientHeight;
    const hasHorizontalScroll = tableContainer && tableContainer.scrollWidth > tableContainer.clientWidth;
    const scrollIndicatorStyle = {
      bottom: this.scrollBarThickness + ((loadMoreStyles && parseFloat(loadMoreStyles.height) + parseFloat(loadMoreStyles.marginTop)) || 0),
      height: !!this.props.virtualize ? Math.min(this.props.virtualize.viewportHeight - headHeight - this.scrollBarThickness, tbody && tbody.getBoundingClientRect().height) : this.wafflesScrollableIndicatorHeight,
      right: (hasVerticalScroll && hasHorizontalScroll) ? this.scrollBarThickness : undefined,
      top: !!this.props.virtualize ? `${headHeight}px` : null,
    };

    let renderedRows: JSX.Element[] = this.state.tableRows;
    if (this.props.virtualize) {
      const start = Math.floor(this.state.scrollTop / this.props.virtualize.rowHeight);
      const end = Math.ceil(start + (this.props.virtualize.viewportHeight / this.props.virtualize.rowHeight));
      renderedRows = this.state.tableRows?.slice(start, end) || [];
    }

    let leftScrollBarClipPath = "none";
    if (this.scrollLeftRef.current) {
      leftScrollBarClipPath = getClipPath(this.scrollLeftRef.current);
    }

    let rightScrollBarClipPath = "none";
    if (this.scrollRightRef.current) {
      rightScrollBarClipPath = getClipPath(this.scrollRightRef.current);
    }

    return (
      <div className={`waffles-outer-container${this.props.virtualize && !this.initialRender ? " virtualized" : ""}${this.props.variant ? ` waffles--${this.props.variant}` : ""}`} style={outerContainerStyle}>
        <div
          className={`waffles-processing-overlay${this.props.loading && !this.props.disableProcessingOverlay ? " show" : ""}`}
          ref={this.processingOverlayRef}
          role="status"
          // This is redundant with role="status" but MDN recommends using both
          aria-live="polite"
          aria-roledescription={`${this.props.loading && !this.props.disableProcessingOverlay ? "table loading in progress" : "table loading complete"}`}
        >
          <div className="qes-icon-spin"><QesIcon iconName={QesIcons.LoadingV1} description="Loading" width="32" color="#00cd87" /* color taken from $qes-tertiary-green-2 */ /></div>
        </div>
        <div className="waffles-resize-position-indicator" ref={this.wafflesResizePositionIndicatorRef} />
        {this.includeFiltering && this.props.columns && (
          <WafflesFilterSystem columns={this.props.columns} onFilterSubmit={(filters: WafflesFilterGroup[]) => this.processChanges(() => this.handleFilterChange(filters))} usedColumns={this.state.usedColumns as string[]} />
        )}

        {!!noResults && !this.initialRender && !this.props.loading ? (
          <>
            <div className="waffles-container" ref={this.tableContainerRef} onScroll={this.handleTableScroll} onMouseUp={this.clearScrollInterval}>
              {this.props.bulkActionBar && <span className={`bulk-action-cell ${WafflesClassNames.td}`}>{this.props.bulkActionBar}</span>}
              {noResults}
            </div>
          </>
        ) : (
          <>
            <div className="waffles-scroll-bar-container" style={scrollBarContainerStyle} id={`${this.props.testId}__waffles-scroll-bar--container`}>
              {/* Horizontal Scroll Left */}
              <div
                className={cx("waffles-scroll-bar", "left-overlay", "left", this.state.containerScrollLeft > 0 ? "" : "hide")}
                ref={this.scrollLeftOverlayRef}
                style={{ ...scrollIndicatorStyle }} />
              <div
                className={cx("waffles-scroll-bar", "left", this.state.containerScrollLeft > 0 ? "" : "hide")}
                id={`${this.props.testId}__waffles-scroll-bar--left`}
                onMouseDown={this.scrollLeft}
                onMouseUp={this.clearScrollInterval}
                ref={this.scrollLeftRef}
                style={{ ...scrollIndicatorStyle, clipPath: leftScrollBarClipPath }}
              />
              {/* Horizontal Scroll Right */}
              <div
                className={cx({
                  "hide": !tableContainer || !(Math.ceil(tableContainer.getBoundingClientRect().width) < tableContainer.scrollWidth) || (Math.ceil(this.state.containerScrollLeft + this.state.containerWidth) >= Math.floor(wafflesTable?.getBoundingClientRect()?.width ?? 0)),
                  "right": true,
                  "right-overlay": true,
                  "waffles-scroll-bar": true,
                })}
                ref={this.scrollRightOverlayRef}
                style={scrollIndicatorStyle}
              />
              <div
                className={cx({
                  "hide": !tableContainer || !(Math.ceil(tableContainer.getBoundingClientRect().width) < tableContainer.scrollWidth) || (Math.ceil(this.state.containerScrollLeft + this.state.containerWidth) >= Math.floor(wafflesTable?.getBoundingClientRect()?.width ?? 0)),
                  "right": true,
                  "waffles-scroll-bar": true,
                })}
                id={`${this.props.testId}__waffles-scroll-bar--right`}
                style={{ ...scrollIndicatorStyle, clipPath: rightScrollBarClipPath }}
                onMouseDown={this.scrollRight}
                onMouseUp={this.clearScrollInterval}
                ref={this.scrollRightRef}
              />
            </div>
            <div className="waffles-container" ref={this.tableContainerRef} onScroll={this.handleTableScroll} style={containerStyle} onMouseUp={this.clearScrollInterval}>
              {this.state.fakeTableHead}

              <table
                className={cx({
                  "invisible": this.props.loading && this.props.data.length === 0,
                  "virtualized": this.props.virtualize && !this.initialRender,
                  [this.getThemeClass(this.props.theme)]: !!this.props.theme,
                  [WafflesClassNames.table]: true,
                })}
                data-testid={this.props.testId}
                style={tableStyle}
              >
                {this.state.tableHead}
                <tbody>
                  {renderedRows}
                </tbody>
              </table>
            </div>
            {this.props.enablePagination && this.props.paginationProps && (
              <WafflesPagination
                changePage={this.props.paginationProps.changePage}
                changeRecordsPerPage={this.props.paginationProps.changeRecordsPerPage}
                enableBackendPagination={this.props.paginationProps.enableBackendPagination}
                handlePageSizeChange={this.props.paginationProps.handlePageSizeChange}
                initialPage={this.props.paginationProps.initialPage}
                pageSizeOptions={this.props.paginationProps.pageSizeOptions}
                recordsPerPage={this.props.paginationProps.recordsPerPage}
                resetToFirstPage={this.props.paginationProps.resetToFirstPage}
                showPageSizeSelector={this.props.paginationProps.showPageSizeSelector}
                skip={this.props.paginationProps.skip}
                totalRecords={this.props.paginationProps.totalRecords}
              />
            )}
          </>
        )}
        {/* Load More Button */}
        {this.state.showLoadMore && !this.props.infiniteScroll && !this.props.loading && !noResults && (
          <QESButton
            className="waffles-load-more"
            onClick={this.handleLoadMore}
            qesType="primary"
            qesRef={this.loadMoreButtonRef}
            qesTestId="waffles-load-more"
          >
            {this.props.loadMoreLabel ?? "Load More"}
          </QESButton>
        )
        }
      </div>
    );
  }

  /* RENDER METHODS */
  private buildHeaders = (usedColumns: Array<keyof T>, sortedColumn?: keyof T, sortDirection?: SortDirections, containerWidth?: number) => {
    const colSpans: Map<string, number> = new Map<string, number>();
    if (this.props.columnGroups) {
      this.props.columnGroups.forEach((cg) => {
        colSpans.set(cg.key, cg.children.filter((child) => usedColumns.includes(child)).length);
      });
    }

    // see above - building the headers is a lot on each render, and could be done elsewhere based on stored information
    const newColumnWidths = this.recordColumnWidths(usedColumns);
    const fakeHeader = !this.props.disableStickyHeader && !this.props.virtualize ? this.getFakeHeader(usedColumns, colSpans, sortedColumn, sortDirection, containerWidth, newColumnWidths) : null;
    const thead = this.buildTHead(usedColumns, false, colSpans, sortedColumn, sortDirection, newColumnWidths);

    return { fakeHeader, thead };
  }

  private buildTBody = (usedColumns: Array<keyof T>, records: Map<string, T[]>) => {
    const toRender: JSX.Element[] = [];
    let startingIndex = 0;
    [...records.keys()].map((key, index, array) => {
      // in order to prevent adding ids with spaces to the HTML
      const idKey = key.replace(/\s+/g, "");

      if (array.length > 1) {
        const rowHeight = this.props.virtualize ? this.props.virtualize.rowHeight : null;
        const rowStyle = this.props.virtualize && this.tableContainerRef.current ?
          {
            height: rowHeight,
            top: (startingIndex * rowHeight) + this.tableContainerRef.current.querySelector("thead").clientHeight,
            width: this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table}`).getBoundingClientRect().width,
          } : null;
        startingIndex += 1;

        toRender.push(
          <tr className={`${WafflesClassNames.tr} category`} key={`${idKey}-title`} style={rowStyle}>
            {/* colSpan 3 covers the toggle column, checkbox column, and title column, so none of those get stretched out.
              * Then, usedColumns.length covers the rest of the table (and then some, which isn't rendered). This lets the sticky class work
              */}
            <td className={`${WafflesClassNames.td} sticky`} colSpan={3}>
              {key}
            </td>
            <td className={WafflesClassNames.td} colSpan={usedColumns.length}>&nbsp;</td>
          </tr>,
        );
      }

      toRender.push(...this.renderBodySegment(usedColumns, records.get(key), startingIndex));

      startingIndex = toRender.length;
    });
    return toRender;
  }

  private buildTHead = (usedColumns: Array<keyof T>, fake: boolean, colSpans: Map<string, number>, sortedColumn: keyof T = this.state.sortedColumn, sortDirection: SortDirections = this.state.sortDirection, columnWidths: Map<keyof T, string> = this.state.columnWidths) => {
    const someSticky = !!this.props.columns ? Object.values(this.props.columns).some((col) => col.sticky) : false;

    const getColumnInfo = (col: keyof T): WafflesColumn => {
      return (this.props.columns && this.props.columns[col as string]) || {
        alignment: TextAlignment.Left,
        displayElement: null,
        displayText: col as string,
        hidden: false,
        noFilter: true,
        noResize: false,
        noSort: false,
        specialSort: null,
        sticky: false,
        testId: "",
      };
    };

    // A page whose rows all have disabled checkboxes has nothing to select, so the bulk checkbox is unchecked and disabled
    const hasNoSelectableRows = this.props.data.length > 0 && this.props.data.every((d) => d.rowIsDisabled || d.checkBoxIsDisabled);
    const bulkCheckbox = (isFake?: boolean) => (
      <th
        className={cx({
          [WafflesClassNames.th]: true,
          "fake-th": fake,
          "hide": !this.props.showCheckboxes,
          "no-sort waffles-bulk-checkbox": true,
          "sticky": someSticky,
        })}
        data-testid={`${this.props.testId}-bulk-checkbox${isFake ? "--fake" : ""}`}
      >
        {!this.props.hideBulkCheckbox && (
          <QESCheckbox
            checked={this.props.bulkCheckboxCheckedOverride !== undefined
              ? this.props.bulkCheckboxCheckedOverride
              : !hasNoSelectableRows && this.props.selectedKeys?.length === ((this.props.totalNumberOfRecords || this.numberOfRecords) - this.props.data.filter((d) => d.rowIsDisabled || d.checkBoxIsDisabled).length)}
            disabled={hasNoSelectableRows}
            onChange={this.handleBulkCheckboxClick}
            indeterminate={this.props.bulkCheckboxIndeterminateOverride !== undefined
              ? this.props.bulkCheckboxIndeterminateOverride
              : (this.props.selectedKeys?.length > 0 && this.props.selectedKeys?.length !== ((this.props.totalNumberOfRecords || this.numberOfRecords) - this.props.data.filter((d) => d.rowIsDisabled || d.checkBoxIsDisabled)?.length))}
            label=""
            testId={`waffles-bulk-checkbox--${this.props.testId}${isFake ? "--fake" : ""}`}
            containerClassName={this.props.showExpansionCheckboxesOnly ? "hide-bulk-checkbox" : ""}
          />
        )}
      </th>
    );

    const bulkCheckboxPlaceholder = (isFake?: boolean) => (
      <th
        className={`${WafflesClassNames.th} no-sort waffles-head-group ${fake ? "fake-th" : ""} ${someSticky ? "sticky" : ""}`}
        data-testid={`${this.props.testId}-bulk-checkbox-placeholder${isFake ? "--fake" : ""}`}
      />
    );

    const sortArrows = (col: keyof T, columnTestId: string, isVerticalHeader: boolean): JSX.Element => {
      const columnInfo: WafflesColumn = getColumnInfo(col);

      let iconProps: { iconDescription: string, iconName: QesIcons | null };
      if ((col === sortedColumn && sortDirection === SortDirections.Desc && !isVerticalHeader) || (col === sortedColumn && sortDirection === SortDirections.Asc && isVerticalHeader)) {
        iconProps = { iconDescription: "Sort descending", iconName: QesIcons.ArrowNarrowDown };
      }
      else if ((col === sortedColumn && sortDirection === SortDirections.Asc && !isVerticalHeader) || (col === sortedColumn && sortDirection === SortDirections.Desc && isVerticalHeader)) {
        iconProps = { iconDescription: "Sort ascending", iconName: QesIcons.ArrowNarrowUp };
      }
      else if (col !== sortedColumn || sortDirection === SortDirections.None) {
        iconProps = { iconDescription: "Sort", iconName: QesIcons.SwitchVerticalV1 };
      }

      return (
        <span className="waffles-sort-indicator">
          <QESButton
            qesType="none"
            qesTestId={`${columnTestId}-caret-sort-button${fake ? "--fake" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              this.processChanges(this.props.customSort ? () => this.handleCustomSort(col, this.getNewSortDirection(col)) : () => this.handleInternalSort(col))
            }}
            className={cx({
              "active": col === sortedColumn && sortDirection !== SortDirections.None
            })}
          >
            <QesIcon iconName={iconProps.iconName} description={iconProps.iconDescription} width="16" />
          </QESButton>
          {columnInfo.specialSort && col === sortedColumn && sortDirection !== SortDirections.None &&
            <span
              className={cx({
                "special-sort--horizontal": !isVerticalHeader,
                "special-sort--vertical": isVerticalHeader,
              })}
              onClick={(e) => handleSpecialSort(e, columnInfo, col)}
            >
              {columnInfo.specialSort}
            </span>
          }
        </span>
      );
    };

    const handleSpecialSort = async (e: React.MouseEvent<HTMLSpanElement>, columnInfo: WafflesColumn, col: keyof T) => {
      e.stopPropagation();
      await columnInfo.handleSpecialSort(col.toString());

      this.handleCustomSort(col, this.state.sortDirection);
    };

    const totalRows = this.getTotalRows();
    const columnGroups = this.props.columnGroups && colSpans?.size > 0 && (
      <tr className={`waffles-head-row ${fake ? "fake-tr" : ""} ${this.props.hideTableHead ? "visuallyhidden" : ""}`}>
        {/* Space for Expansion Toggle Column */}
        {!!this.state.expansionData && !!this.state.expansionData.size && (
          <th
            className={cx({
              "fake-th": fake,
              "no-sort waffles-head-group": true,
              "sticky": someSticky,
              [WafflesClassNames.th]: true,
            })}
            data-testid={`${this.props.testId}__group-row-expansion-space`}
          />
        )}
        {this.props.showCheckboxes ?
          this.props.useVerticalHeaders ?
            bulkCheckbox(fake) :
            bulkCheckboxPlaceholder(fake) :
          null
        }
        {this.props.columnGroups.map((group, index) => {
          const groupStyle = {
            textAlign: group.alignment ? group.alignment as "left" | "center" | "right" : null,
          };

          const includeSortArrows: boolean = this.props.useVerticalHeaders && group.children.length === 1;

          let columnTestId = "";
          let verticalHeaderSorting: JSX.Element = null;
          if (includeSortArrows) {
            const columnInfo: WafflesColumn = getColumnInfo(group.children[0]);
            const noSort = columnInfo.noSort || totalRows <= 1;

            columnTestId = columnInfo.testId ?
              `${this.props.testId}__${columnInfo.testId}${fake ? "--fake" : ""}` :
              `${this.props.testId}__col-head-${(columnInfo.displayText !== null && columnInfo.displayText !== undefined) ? columnInfo.displayText : group.children[0] as string}${fake ? "--fake" : ""}`;

            verticalHeaderSorting = !noSort && sortArrows(
              group.children[0],
              columnTestId,
              false,
            );
          }

          return !!colSpans.get(group.key) &&
            (
              <th
                className={cx({
                  "fake-th": fake,
                  "no-sort": true,
                  "sticky": this.props.columns && Object.entries(this.props.columns).some((col) => group.children.includes(col[0] as keyof T) && col[1].sticky),
                  [WafflesClassNames.th]: true,
                  "waffles-head-group": true,
                })}
                colSpan={colSpans.get(group.key)}
                key={index}
                data-key={group.key}
                data-testid={`${this.props.testId}__${group.testId}${fake ? "--fake" : ""}`}
                style={groupStyle}
              >
                {!group.hideKey && (
                  <div className="waffles-head-content">
                    {
                      group.showInfoIcon ? (
                        <>
                          <TooltipWrapper text={group.tooltip} position="top" containerClassName="waffles-tooltip-indicator">
                            <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" />
                          </TooltipWrapper>
                          <TooltipWrapper containerClassName="waffles-head-title" delay={200} text={group.key} position={"top"}>
                            <span>{group.key}</span>
                          </TooltipWrapper>
                        </>
                      ) :
                        <TooltipWrapper containerClassName="waffles-head-title" delay={200} text={group.tooltip ?? group.key} position={"top"}>
                          <span>{group.key}</span>
                        </TooltipWrapper>
                    }
                    {verticalHeaderSorting}
                  </div>
                )}
              </th>
            );
        })}
      </tr>
    );

    const columns = (
      <tr className={`waffles-head-row ${fake ? "fake-tr" : ""} ${this.props.hideTableHead ? "visuallyhidden" : ""}`}>
        {/* Space for Expansion Toggle Column */}
        {!!this.state.expansionData && !!this.state.expansionData.size && (
          <th
            className={cx({
              [WafflesClassNames.th]: true,
              "fake-th content-box": fake,
              "no-sort toggle": true,
              "sticky": someSticky,
            })}
          >
            <span style={{ display: "inline-block", width: "43px" }} />
          </th>
        )}

        {/* Bulk Checkbox */}
        {this.props.showCheckboxes && (this.props.useVerticalHeaders ? bulkCheckboxPlaceholder(fake) : bulkCheckbox(fake))}

        {/* Column Headers */}
        {usedColumns.map((col) => {
          const columnInfo: WafflesColumn = getColumnInfo(col);

          // will need to be updated when props.preSortedCompareMode is merged in
          const noSort = columnInfo.noSort || totalRows <= 1 || (totalRows <= 2 && !!this.props.compareData);
          const isSticky = columnInfo.sticky;
          const showResize = !this.props.columns || !columnInfo.noResize;

          const headStyle = {
            textAlign: columnInfo.alignment ? columnInfo.alignment as "left" | "center" | "right" : null,
          };
          const contentStyle = {
            paddingLeft: columnInfo.paddingLeft ? `${columnInfo.paddingLeft}px` : null,
            width: columnWidths.has(col)
              ? columnInfo.sizeContentToFit && !fake ? '100%' : `${columnWidths.get(col)}px`
              : "auto"
          } as React.CSSProperties;
          const displayText: string = (columnInfo.displayText !== null && columnInfo.displayText !== undefined) ? columnInfo.displayText : col.toString();
          const columnTestId = columnInfo.testId ?
            `${this.props.testId}__${columnInfo.testId}${fake ? "--fake" : ""}` :
            `${this.props.testId}__col-head-${displayText as string}${fake ? "-fake" : ""}`;


          const titleContent = (
            <span
              className={cx(
                "waffles-head-title",
                {
                  "selected": columnInfo?.isSelected,
                  "waffles-head-title--vertical--sortable": this.props.useVerticalHeaders && !noSort,
                  "waffles-head-title--vertical--tooltip": this.props.useVerticalHeaders && !!columnInfo.tooltip,
                }
              )}
              style={contentStyle}
              data-testid={`${columnTestId}-title-sort${fake ? "--fake" : ""}`}
            >
              {
                columnInfo.tooltip ? (
                  <>
                    <TooltipWrapper text={columnInfo.tooltip} position="bottom">
                      <span className={cx(
                        this.props.useVerticalHeaders ? "vertical-waffles-tooltip-indicator" : "waffles-tooltip-indicator"
                      )}>
                        <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" />
                      </span>
                    </TooltipWrapper>
                    {columnInfo.displayElement || displayText}
                  </>
                ) : columnInfo.displayElement || displayText
              }
            </span>
          );


          return (
            <th
              className={cx({
                "fake-th": fake,
                "no-horizontal-padding": columnInfo.noHorizontalPadding,
                "no-sort": noSort,
                "sticky": isSticky,
                "vertical": this.props.useVerticalHeaders,
                [WafflesClassNames.th]: true,
              })}
              data-key={col}
              data-testid={columnTestId}
              key={col as string}
              style={columnInfo.style ? {
                ...headStyle,
                ...columnInfo.style,
              } : headStyle}
            >
              <div
                className={cx(
                  !this.props.useVerticalHeaders ? "horizontal-header-title" : "vertical-header-title",
                  columnInfo.tooltip ? "waffles-head-content--tooltip" : "waffles-head-content"
                )}
                onClick={() => !noSort && (this.processChanges(this.props.customSort ? () => this.handleCustomSort(col, this.getNewSortDirection(col)) : () => this.handleInternalSort(col)))}
              >
                {(!noSort && this.props.useVerticalHeaders && columnInfo.displayText) &&
                  sortArrows(col, columnTestId, true)
                }
                {(columnInfo.displayElement && columnInfo.tooltip) ? (
                  <TooltipWrapper
                    delay={200}
                    text={columnInfo.tooltip === undefined ? displayText.toString() : columnInfo.tooltip}
                    position={"bottom"}
                  >

                    {titleContent}
                  </TooltipWrapper>) :
                  titleContent
                }
                {!noSort && !this.props.useVerticalHeaders && sortArrows(col, columnTestId, false)}
              </div>
              {showResize && (
                <div
                  className="waffles-col-resizer"
                  data-testid={`${columnTestId}-resizer${fake ? "--fake" : ""}`}
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => this.handleColumnResize(event, col)}
                />)
              }
            </th>
          );
        })}
      </tr>
    );

    return (
      <thead className={fake ? "fake-thead" : ""}>
        {/* Bulk Action */}
        {this.props.bulkActionBar && (
          <tr className={`${WafflesClassNames.tr} bulk-action`}>
            {/* Space for Expansion Toggle Column */}
            {!!this.state.expansionData && !!this.state.expansionData.size && <td className={`bulk-action-cell ${WafflesClassNames.td} ${someSticky ? "sticky" : ""}`} />}
            {this.props.showCheckboxes && <td className={`bulk-action-cell ${WafflesClassNames.td} ${someSticky ? "sticky" : ""}`} />}
            <td
              colSpan={usedColumns.length}
              className={`bulk-action-cell ${WafflesClassNames.td}`}
            >
              {/* This span is sticky, which allows us to stick the bulk action stuff despite the td being the width of the table */}
              <span className="bulk-action-content">
                {this.props.bulkActionBar}
              </span>
            </td>
          </tr>
        )}

        {this.props.useVerticalHeaders ?
          <>
            {columns}
            {columnGroups}
          </> :
          <>
            {columnGroups}
            {columns}
          </>
        }
      </thead>
    );
  }

  private getFakeHeader = (usedColumns: Array<keyof T>, colSpans: Map<string, number>, sortedColumn?: keyof T, sortDirection?: SortDirections, containerWidth: number = this.state.containerWidth, columnWidths: Map<keyof T, string> = this.state.columnWidths) => (
    <table className={`fake-table ${this.getThemeClass(this.props.theme)}`} style={{ width: containerWidth }}>
      {this.buildTHead(usedColumns, true, colSpans, sortedColumn, sortDirection, columnWidths)}
    </table>
  )

  private getTotalRows = () => {
    const length = this.props.data.length;
    if (this.props.preSortedCompareMode) {
      return length / 2;
    }
    return length;
  }

  private renderBodySegment = (usedColumns: Array<keyof T>, rows: T[], startingIndex?: number): JSX.Element[] => {
    // need to copy rows so we don't update state directly
    const rowsCopy: T[] = [...rows];
    if (!this.initialRender && this.state.expansionData) {
      // iterate over rows to keep the index straight, but add expansions to rowsCopy so we can render them
      rows.forEach((row, index, rowsArr) => {
        const localKey = row[wafflesKeyColumnName];
        if (this.state.expansionData.get(localKey) &&
          (!this.props.compareData || (!!this.state.compareMode && index % 2 !== 0)) &&
          (!rowsArr[index + 1] || !rowsArr[index + 1][wafflesKeyColumnName].includes("waffles-expansion:"))
        ) {
          const expandedRows = [...this.state.expansionData.get(localKey)];
          expandedRows.forEach((er) => {
            er.isExpansion = true;
            er.parentKey = localKey;
            er[wafflesKeyColumnName] = er[wafflesKeyColumnName].includes("waffles-expansion:") ? er[wafflesKeyColumnName] : `waffles-expansion:${er[wafflesKeyColumnName]}`;
          });
          const indexAddition = this.state.compareMode ? 2 : 1;
          rowsCopy.splice(rowsCopy.findIndex((r) => r[wafflesKeyColumnName] === localKey) + indexAddition, 0, ...expandedRows);
        } else if ((rowsArr[index + 1] && rowsArr[index + 1][wafflesKeyColumnName].includes("waffles-expansion:")) &&
          this.state.expansionData.has(localKey)
        ) {
          const expandedRows = this.state.expansionData.get(localKey);
          rowsCopy.splice(index + 1, expandedRows.length);
        }
      });
    }

    const renderedPanelKeys = new Set<string>();
    const bodySegment = rowsCopy.map((row, rowIndex) => {
      // The index parameter of map isn't reliable here due to the key prop in datum
      // Start it at -1 so we can increment as soon as we determine the cell is useful (to stay 0-indexed)
      let cellIndex = -1;
      // let's leave this key as the index, so sorting doesn't change _keys_ - it also serves as positioning for virtualization
      const rowKey = (startingIndex + rowIndex).toString();
      // and for this one we need to use the actual data key
      let key = row[wafflesKeyColumnName];
      const columnsToCompare = this.props.compareModeKeyColumns !== undefined ? this.props.compareModeKeyColumns : (!!this.props.compareData ? 1 : 0);
      const isExpansion = key.includes("waffles-expansion:");
      // todo - consider preSortedCompareMode
      const isComparedRow = this.state.compareMode && (!!columnsToCompare || this.props.compareModeKeyColumns !== undefined) && rowIndex % 2 !== 0;
      if (isComparedRow) {
        key = `${key}-compared`;
      }

      const headerContentEl = this.tableContainerRef.current && this.tableContainerRef.current.querySelector(".waffles-head-content");
      const headerTitleEl = this.tableContainerRef.current && this.tableContainerRef.current.querySelector(".waffles-head-title");
      const headerContentStyles = headerContentEl && window.getComputedStyle(headerContentEl);
      const headerTitleStyles = headerTitleEl && window.getComputedStyle(headerTitleEl);
      const extraSpace = headerContentStyles && parseFloat(headerContentStyles.paddingLeft) + parseFloat(headerContentStyles.paddingRight) + parseFloat(headerTitleStyles.marginRight);
      this.headerExtraSpace = extraSpace ? extraSpace : this.headerExtraSpace;
      const rowHeight = this.props.virtualize ? this.props.virtualize.rowHeight : null;
      const thead = this.tableContainerRef.current && this.tableContainerRef.current.querySelector("thead");

      let rowStyle = {};
      if (this.props.virtualize) {
        rowStyle = { height: rowHeight, top: (parseInt(rowKey, 10) * rowHeight) + (thead ? thead.clientHeight : this.tableHeadHeight) };
      } else {
        rowStyle = null;
      }

      const auxCellStyle = {
        borderBottomWidth: !isComparedRow && this.state.compareMode ? 0 : 2,
        height: this.props.virtualize ? rowHeight : "auto",
        width: 44,
      };

      let shouldShowCheckbox = false;
      if (!!this.state.expansionData?.size) {
        if (isExpansion) {
          if (this.props.dataExpansionsHaveIndividualCheckboxes) {
            shouldShowCheckbox = true;
          }
        } else {
          shouldShowCheckbox = true;
        }
      } else {
        shouldShowCheckbox = this.props.showCheckboxes;
      }

      let expansionGroupKey = "";
      if (!!this.state.expansionData?.size) {
        if (isExpansion) {
          let mostRecentExpansionParent;
          let searchIndex = rowIndex - 1;
          // in case a row key shows up in multiple expansions, find the most recent expansion parent
          // that way opening expansion a doesn't suddenly show a row under the closed expansion b
          while (mostRecentExpansionParent === undefined && searchIndex > -1) {
            if (rowsCopy[searchIndex][wafflesKeyColumnName].includes("waffles-expansion:")) {
              searchIndex -= 1;
            } else {
              mostRecentExpansionParent = rowsCopy[searchIndex];
            }
          }
          expansionGroupKey = `${mostRecentExpansionParent[wafflesKeyColumnName]}-row`;
        } else {
          expansionGroupKey = row[wafflesKeyColumnName];
        }
      }

      const expansionKeys = !!this.state.expansionData.has(key) ? this.state.expansionData.get(key).map((d) => d[wafflesKeyColumnName]) : [];
      const expansionChildren = expansionKeys.map((expansionKey) => this.tableContainerRef.current?.querySelector(`tr[data-key="waffles-expansion:${expansionKey}"] input[type="checkbox"]`));
      const isExpansionParentAndChildrenHaveCheckboxes = expansionChildren.length > 0 && this.props.dataExpansionsHaveIndividualCheckboxes;

      const isExpansionParentAndAllChildrenAreSelected = isExpansionParentAndChildrenHaveCheckboxes && expansionChildren.every((checkbox) => !!checkbox && this.props.selectedKeys.includes(checkbox.closest("tr").getAttribute("data-key").replace("waffles-expansion:", "")));
      const isExpansionParentAndShouldBeIndeterminate = isExpansionParentAndChildrenHaveCheckboxes && !isExpansionParentAndAllChildrenAreSelected && expansionChildren.some((checkbox) => !!checkbox && this.props.selectedKeys.includes(checkbox.closest("tr").getAttribute("data-key").replace("waffles-expansion:", "")));

      const keyWithoutExpansionPrefix = key.replace("waffles-expansion:", "");

      const parentClassNames: string = this.props.rowDecorator ? this.props.rowDecorator(row, isComparedRow) : "";
      const someSticky = this.props.columns && Object.values(this.props.columns).some((col) => col.sticky);
      const rowDisabled = row.rowIsDisabled;
      const checkBoxDisabled = row.checkBoxIsDisabled;

      let iconProps: { iconDescription: string, iconName: QesIcons | null };
      if (this.state.expansionOpenStatusMap.get(key) === WafflesExpansionToggleStatus.Loading) {
        iconProps = { iconDescription: "Loading", iconName: QesIcons.LoadingV1 };
      }
      else if (this.state.expansionOpenStatusMap.get(key) === WafflesExpansionToggleStatus.Open) {
        iconProps = { iconDescription: "Collapse", iconName: QesIcons.ChevronUp };
      }
      else if (this.state.expansionOpenStatusMap.get(key) === WafflesExpansionToggleStatus.Closed) {
        iconProps = { iconDescription: "Expand", iconName: QesIcons.ChevronDown };
      }

      const onRowClick = (event: React.MouseEvent, row: T) => {
        const target = event.target as HTMLElement;
        const stopPropagation = (target.closest("button, input, a, [role='button']"));
        if (!!this.props.onRowClick && !rowDisabled && !stopPropagation) {
          this.props.onRowClick(row);
        }
      };

      if (isExpansion && this.props.renderExpansionPanel) {
        const panelParentKey = row.parentKey;
        if (renderedPanelKeys.has(panelParentKey)) {
          return null;
        }
        renderedPanelKeys.add(panelParentKey);
        const isVisible = this.state.expansionOpenStatusMap.get(panelParentKey) === WafflesExpansionToggleStatus.Open;
        const colSpan = usedColumns.length
          + (!!this.state.expansionData?.size ? 1 : 0)
          + (this.props.showCheckboxes ? 1 : 0);
        return (
          <tr
            className={cx(
              expansionGroupKey.replace(/:/g, "-"),
              {
                "collapse": true,
                "show": isVisible,
                [WafflesClassNames.tr]: true,
              }
            )}
            data-key={key}
            data-testid={`${this.props.testId}__expansion-panel-${rowKey}`}
            key={rowKey}
          >
            <td
              className={WafflesClassNames.td}
              colSpan={colSpan}
              style={{ padding: 0 }}
            >
              {this.props.renderExpansionPanel(panelParentKey)}
            </td>
          </tr>
        );
      }

      return (
        <tr
          className={cx(
            // TS won't accept the template string as an object key, so we insert it like this...
            isExpansion && expansionGroupKey.replace(/:/g, "-"), {
            "clickable-row": !!this.props.onRowClick && !rowDisabled,
            "collapse": isExpansion,
            "compare-row": isComparedRow,
            "disabled": rowDisabled,
            "expansion-panel-open": !isExpansion && !!this.props.renderExpansionPanel && !!this.state.expansionData?.has(key) && this.state.expansionOpenStatusMap.get(key) === WafflesExpansionToggleStatus.Open,
            [parentClassNames]: !!parentClassNames && !isExpansion,
            "show": isExpansion && this.state.expansionOpenStatusMap.get(row.parentKey) === WafflesExpansionToggleStatus.Open,
            [WafflesClassNames.tr]: true,
          })}
          key={rowKey}
          data-key={key}
          onClick={(e) => onRowClick(e, row)}
          onMouseEnter={!!this.props.onRowMouseOver ? (() => this.props.onRowMouseOver(row)) : null}
          onMouseLeave={!!this.props.onRowMouseOver ? (() => this.props.onRowMouseOver(null)) : null}
          style={rowStyle}
          data-testid={`${this.props.testId}__row-${rowKey}`}
        >
          {/* Expansion toggle column */}
          {!!this.state.expansionData?.size && (
            isExpansion ?
              <td
                className={cx({
                  "compared": this.state.compareMode,
                  "sticky": someSticky,
                  [WafflesClassNames.td]: true,
                  "toggle": true,
                })}
                key={`${rowKey}:toggle`}
                style={auxCellStyle}
              >
                <span>&nbsp;</span>
              </td>
              : (
                <td
                  className={cx({
                    "compared": this.state.compareMode,
                    "sticky": someSticky,
                    [WafflesClassNames.td]: true,
                    "toggle": true,
                  })}
                  key={`${rowKey}:toggle`}
                  style={auxCellStyle}
                >
                  {this.state.expansionData.has(key) && !rowDisabled && (
                    <QESButton
                      qesType="none"
                      qesTestId={`${expansionGroupKey}-toggle`}
                      onClick={() => this.onDataExpansionToggle(expansionGroupKey)}
                      className="p-0"
                    >
                      <QesIcon className={this.state.expansionOpenStatusMap.get(key) === WafflesExpansionToggleStatus.Loading ? "qes-icon-spin" : ""} iconName={iconProps.iconName} description={iconProps.iconDescription} color={QesColors.QesPrimaryGray8} />
                    </QESButton>
                  )}
                </td>
              )
          )}

          {/* Checkbox column */}
          <td
            className={cx({
              [WafflesClassNames.td]: true,
              "checkbox": true,
              "compared": this.state.compareMode,
              "hide": !this.props.showCheckboxes,
              "sticky": someSticky,
            })}
            key={`${rowKey}:checkbox`}
            style={auxCellStyle}
          >
            {((shouldShowCheckbox && isExpansion) || (shouldShowCheckbox && !this.props.showExpansionCheckboxesOnly && !isExpansion)) && (
              <QESCheckbox
                label=""
                checked={!rowDisabled && !checkBoxDisabled &&
                  (this.props.selectedKeys && this.props.selectedKeys.includes(keyWithoutExpansionPrefix) &&
                    !this.props.indeterminateKeys?.includes(keyWithoutExpansionPrefix) &&
                    ((!isExpansionParentAndChildrenHaveCheckboxes && this.props.dataExpansionsHaveIndividualCheckboxes) || !this.props.dataExpansionsHaveIndividualCheckboxes)) ||
                  isExpansionParentAndAllChildrenAreSelected
                }
                indeterminate={isExpansionParentAndShouldBeIndeterminate || this.props.indeterminateKeys?.includes(keyWithoutExpansionPrefix)}
                onChange={() => this.handleCheckboxClick(keyWithoutExpansionPrefix, isExpansionParentAndChildrenHaveCheckboxes, isExpansionParentAndAllChildrenAreSelected)}
                disabled={rowDisabled || !this.props.showCheckboxes || checkBoxDisabled}
                testId={`${this.props.testId}__checkbox--${rowKey}`}
              />
            )}
          </td>

          {Object.entries(row).map(([colName, cellValue]) => {
            if (!usedColumns.includes(colName as keyof T)) {
              return null;
            }
            cellIndex += 1;
            // todo - consider preSortedCompareMode
            const isCompareCell = !!columnsToCompare && cellIndex < columnsToCompare && this.state.compareMode;
            const isCompareCellAndRow = isCompareCell && isComparedRow;
            const contentCellStyle = {
              borderBottomWidth: isCompareCell && !isComparedRow ? 0 : 2,
              height: rowHeight,
              textAlign: this.props.columns && this.props.columns[colName] && this.props.columns[colName].alignment ? this.props.columns[colName].alignment as "left" | "center" | "right" : null,
            };
            const sizeToFit = (this.props.columns && this.props.columns[colName] && this.props.columns[colName].sizeContentToFit);
            let width = this.state.columnWidths.has(colName as keyof T) ? `${this.state.columnWidths.get(colName as keyof T)}px` : "auto";
            if (!!this.props.virtualize && !this.props.columns[colName]?.noSort && parseFloat(width) > 0) {
              // virtualized columns need to account account for the 0.25rem sort indicator width when present
              const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
              width = `${parseFloat(width) + (0.25 * remSize)}px`;
            }
            if (sizeToFit)
              width = "100%";

            const cellSizerStyle = {
              maxWidth: (this.props.columns && this.props.columns[colName] && this.props.columns[colName].maxWidth ? this.props.columns[colName].maxWidth : "auto"),
              minWidth: (this.props.columns && this.props.columns[colName] && this.props.columns[colName].minWidth ? this.props.columns[colName].minWidth : "0"),
              whiteSpace: (this.props.columns && this.props.columns[colName] && this.props.columns[colName].wrapContent ? "normal" : "auto"),
              width: width,
            } as React.CSSProperties;

            const isSticky = this.props.columns && this.props.columns[colName as string] && this.props.columns[colName as string].sticky;
            const cellContent = (
              <div className="waffles-cell-content-sizer" style={cellSizerStyle}>
                {/* Need to do hard checks here, because sometimes we like rendering the number 0 */}
                {cellValue !== null && cellValue !== undefined && !isCompareCellAndRow ? cellValue : ""}
              </div>
            );

            const columnInfo = !!this.props.columns && this.props.columns[colName];

            if (this.props.keyColumns?.includes(colName)) {
              return (
                <th
                  className={cx({
                    [WafflesClassNames.td]: true,
                    "compared": isCompareCell,
                    "no-horizontal-padding": columnInfo?.noHorizontalPadding,
                    "selected": columnInfo?.isSelected,
                    "sticky": isSticky,
                    "vertical-waffles-cell": this.props.useVerticalHeaders,
                  })}
                  key={cellIndex}
                  data-key={colName}
                  scope="row"
                  style={columnInfo?.style ? {
                    ...contentCellStyle,
                    ...columnInfo.style,
                  } : contentCellStyle}
                >
                  {cellContent}
                </th>
              );
            }

            return (
              <td
                className={cx({
                  [WafflesClassNames.td]: true,
                  "compared": isCompareCell,
                  "no-horizontal-padding": columnInfo?.noHorizontalPadding,
                  "selected": columnInfo?.isSelected,
                  "sticky": isSticky,
                  "vertical-waffles-cell": this.props.useVerticalHeaders,
                })}
                key={cellIndex}
                data-key={colName}
                style={columnInfo?.style ? {
                  ...contentCellStyle,
                  ...columnInfo.style,
                } : contentCellStyle}
              >
                {cellContent}
              </td>
            );
          })}
        </tr>
      );
    });

    return bodySegment;
  }

  private onDataExpansionToggle = (expansionGroupKey: string) => {
    const newExpansionStatusMap = new Map(this.state.expansionOpenStatusMap);
    const currentStatus = newExpansionStatusMap.get(expansionGroupKey);

    const fixScrollBars = () => {
      const newHeight = this.tableContainerRef.current.querySelector("tbody").getBoundingClientRect().height;
      this.scrollLeftRef.current.style.height = `${newHeight}px`;
      this.scrollRightRef.current.style.height = `${newHeight}px`;
      if (this.scrollLeftOverlayRef.current) {
        this.scrollLeftOverlayRef.current.style.height = `${newHeight}px`;
      }
      if (this.scrollRightOverlayRef.current) {
        this.scrollRightOverlayRef.current.style.height = `${newHeight}px`;
      }
    }

    switch (currentStatus) {
      case WafflesExpansionToggleStatus.Open:
        newExpansionStatusMap.set(expansionGroupKey, WafflesExpansionToggleStatus.Closed);
        this.setState({
          expansionOpenStatusMap: newExpansionStatusMap,
        }, fixScrollBars);
        return;
      case WafflesExpansionToggleStatus.Closed:
        if (this.state.expansionData.get(expansionGroupKey)?.length > 0) {
          newExpansionStatusMap.set(expansionGroupKey, WafflesExpansionToggleStatus.Open);
          this.setState({
            expansionOpenStatusMap: newExpansionStatusMap,
          }, fixScrollBars);
        } else {
          newExpansionStatusMap.set(expansionGroupKey, WafflesExpansionToggleStatus.Loading);
          this.setState({
            expansionOpenStatusMap: newExpansionStatusMap,
          }, async () => {
            this.props.dataExpansionOnToggleClick(expansionGroupKey, this.state.compareMode).then(() => {
              newExpansionStatusMap.set(expansionGroupKey, WafflesExpansionToggleStatus.Open);
              this.setState({
                expansionOpenStatusMap: newExpansionStatusMap,
              }, fixScrollBars);
            })
          });
        }
        return;
      case WafflesExpansionToggleStatus.Loading:
      default:
        return;
    }
  }

  /* BUILD METHODS */
  private buildComparedRecords = (newMap: Map<string, T[]>, comparedData: T[], sortDirection?: SortDirections) => {
    if (!!comparedData && comparedData.length > 0 && this.state) {
      comparedData = this.sortColumns(comparedData);
      let tempComparedData = [...comparedData];
      const keys = [...newMap.keys()];
      keys.forEach((mapKey) => {
        if (this.props.rowGroups && this.props.compareRowGroups) {
          tempComparedData = comparedData.filter((f) => this.props.compareRowGroups.has(mapKey) && this.props.compareRowGroups.get(mapKey).includes(f.key));
        }
        const segment = newMap.get(mapKey);
        const newSegment = [...segment];
        segment.forEach((row, index) => {
          // for each row in the normal data, we want to see if there's a compared data row with the same key
          const comparedRow = tempComparedData.find((cd) => row.key === cd.key);
          // if there is, we use .splice to insert the compared row into the segment AFTER the normal data row,
          // and filter the compared row out of the tempComparedData
          if (comparedRow) {
            newSegment.splice((index * 2) + 1, 0, comparedRow);
            tempComparedData = tempComparedData.filter((tempRow) => comparedRow.key !== tempRow.key);
          } else {
            // otherwise, we need something to take that space, so we make a blank row to insert after the normal data row
            const blankRow: T = {} as T;
            Object.keys(segment[0]).forEach((tkey) => {
              (blankRow[tkey as keyof T]) = null;
              blankRow.key = row.key;
            });
            newSegment.splice((index * 2) + 1, 0, blankRow);
          }
        });
        // if there's still data left in tempComparedData, it means the compared data has keys that our normal data does not
        // so in that case, we need to make blank rows for the "normal" data set, insert them,
        // and then insert the leftover compared data rows alongside them
        if (tempComparedData.length > 0) {
          tempComparedData.forEach((tempRow) => {
            const blankRow: T = {} as T;
            Object.keys(tempComparedData[0]).forEach((tkey) => {
              (blankRow[tkey as keyof T]) = null;
            });
            this.state.usedColumns.slice(0, this.props.compareModeKeyColumns !== undefined ? this.props.compareModeKeyColumns : 1).forEach((tkey) => {
              blankRow[tkey as keyof T] = tempRow[tkey as keyof T];
            });
            blankRow.key = tempRow.key;
            newSegment.push(blankRow, tempRow);
          });
        }
        // defaultDisplayOrder informs Waffles how to sort compared data when at least one data set has unique key(s)
        // without, compared row couples that have a blank row on top will always end up at the bottom of the table and won't be sorted,
        // since the provided data doesn't have those keys
        const orderedSegment = this.props.defaultDisplayOrder && (sortDirection || this.state && this.state.sortDirection) === SortDirections.None ? this.sortComparedSegment(newSegment) : newSegment;
        newMap.set(mapKey, orderedSegment);
      });
    }
    return newMap;
  }

  private buildExpansionData = (isCompareMode: boolean) => {
    const expansionData = new Map<string, T[]>();
    const hasExpansions = this.props.dataExpansions && this.props.dataExpansions.size > 0;
    const hasComparedExpansions = this.props.compareExpansions && this.props.compareExpansions.size > 0;
    if (hasExpansions) {
      if (hasComparedExpansions && isCompareMode) {
        [...this.props.dataExpansions.keys()].forEach((key) => {
          const data = this.sortColumns([...this.props.dataExpansions.get(key)]);
          const compareData = this.sortColumns((this.props.compareExpansions.get(key) && [...this.props.compareExpansions.get(key)]) || []);
          const interleaved: T[] = [];
          data.forEach((datum) => {
            let matchingCompareDatum = compareData.find((cd) => cd.key.replace(/(-a|-b)$/, "") === datum.key.replace(/(-a|-b)$/, ""));
            if (!matchingCompareDatum) {
              matchingCompareDatum = {} as T;
              Object.keys(datum).forEach((tkey) => {
                (matchingCompareDatum[tkey as keyof T]) = null;
              });
            }
            const pairKey = datum.key;
            datum.key = datum.key.match(/-a$/) ? datum.key : `${pairKey}-a`;
            matchingCompareDatum.key = matchingCompareDatum.key && matchingCompareDatum.key.match(/-b$/) ? matchingCompareDatum.key : `${pairKey}-b`;
            interleaved.push(datum, matchingCompareDatum);
          });
          expansionData.set(key, interleaved);
        });
      } else {
        this.props.dataExpansions.forEach((expansionRows, expansionKey) => {
          expansionData.set(expansionKey, this.sortColumns(expansionRows));
        });
      }
    }
    return expansionData;
  }

  private sortColumns = (data: T[]) => {
    /* This conditional checks for whether the column config uses sort order.
     * Sort order is useful for when the data comes straight from the API and
     * we don't have any control over the order of the keys (which Waffles usually depends on for column order).
     * So here, if any column has sort order, we sort the column config according to those values (assuming Infinity if there's nothing listed),
     * and extract those keys into an array.
     * Then, we iterate over each row of data and reorder the keys in accordance with that extracted array by basically
     * creating a whole new data set and replacing the existing one.
     */
    if (this.props.columns && Object.values(this.props.columns).some((col) => !!col.sortOrder)) {
      const tempData: T[] = [];
      const orderedKeys = Object.entries(this.props.columns).sort((a, b) => (a[1].sortOrder || Infinity) > (b[1].sortOrder || Infinity) ? 1 : -1).map((colPair) => colPair[0]);
      data.forEach((row) => {
        // this is only a partial because we don't want to make a constructor for T
        const newRow: Partial<T> = {};
        newRow[wafflesKeyColumnName] = row[wafflesKeyColumnName];
        newRow.isExpansion = row.isExpansion;
        newRow.rowIsDisabled = row.rowIsDisabled;
        newRow.checkBoxIsDisabled = row.checkBoxIsDisabled;
        orderedKeys.forEach((key) => {
          newRow[key as keyof T] = row[key as keyof T];
        });
        tempData.push(newRow as T);
      });
      data = tempData;
    }
    return data;
  }

  private buildRecords = (filteredData?: T[], comparedFilteredData?: T[]) => {
    if (!this.props.data.length && !(this.props.compareData && this.props.compareData.length)) {
      return new Map([[wafflesKeyColumnName, this.props.data]]);
    }
    let newMap = new Map<string, T[]>();
    const data = this.sortColumns(filteredData || [...this.props.data]);
    if ((!!this.props.rowGroups && this.props.rowGroups.size) || (!!this.props.compareRowGroups && this.props.compareRowGroups.size)) {
      const allRowGroups = new Map(this.props.rowGroups);
      [...((this.props.compareRowGroups || new Map<string, string[]>()).entries())].forEach((entry) => {
        if (allRowGroups.has(entry[0])) {
          const temp = allRowGroups.get(entry[0]);
          allRowGroups.set(entry[0], [...temp, ...entry[1]]);
        } else {
          allRowGroups.set(entry[0], entry[1]);
        }
      });
      allRowGroups.forEach((value, key) => {
        const groups = value.map((v) => {
          const matchIndex = data.findIndex((d) => {
            return d.key === v;
          });
          // splice removes the item from data and returns it in an array
          // but -1 starts from the end of the array so we need an extra check here
          if (matchIndex < 0) {
            return null;
          }
          return data.splice(matchIndex, 1)[0];
        }).filter((f) => !!f); // filter out nulls
        newMap.set(key, groups);
        if (data.length > 0) {
          newMap.set("Uncategorized", data);
        }
      });
    } else {
      newMap = new Map([["Uncategorized", data]]);
    }

    newMap = this.buildComparedRecords(newMap, comparedFilteredData || this.props.compareData);

    // No point in showing an empty group
    [...newMap.keys()].filter((k) => newMap.get(k).length === 0).forEach((k) => newMap.delete(k));

    return newMap;
  }

  private sortComparedSegment = (segment: T[]) => {
    const sortedSegment: T[] = [];
    const order = [...this.props.defaultDisplayOrder].sort((a, b) => a[1] > b[1] ? 1 : -1);
    order.forEach((o) => {
      const index = segment.findIndex((s) => {
        return ((s.key || "").toString() === o[0]);
      });
      if (index !== -1) {
        if (index % 2 === 0) {
          sortedSegment.push(segment[index], segment[index + 1]);
        } else {
          sortedSegment.push(segment[index - 1], segment[index]);
        }

      }
    });
    return sortedSegment;
  }

  /* EVENT HANDLERS */
  private handleBulkCheckboxClick = () => {
    let updatedSelections: string[] = [];
    let uniqueKeys: string[] = [];
    const renderedSelections = this.getRenderedSelections();
    const numberOfRenderedSelections = renderedSelections.length;
    if (numberOfRenderedSelections !== ((this.props.totalNumberOfRecords || this.numberOfRecords) - this.props.data.filter((d) => d.rowIsDisabled || d.checkBoxIsDisabled).length)) {
      if (this.props.dataExpansionsHaveIndividualCheckboxes) {
        // get all the expansion keys, but only use the ones that are present in the current dataset
        const keys = Array.from(this.state.expansionData.keys()).filter((expansionKey) => this.props.data.find((d) => d[wafflesKeyColumnName] === expansionKey));
        for (const x of this.props.data) {
          if (!keys.includes(x[wafflesKeyColumnName])) {
            updatedSelections.push(x[wafflesKeyColumnName]);
          }
        }

        for (const [key, value] of this.state.expansionData) {
          if (keys.includes(key)) {
            updatedSelections = updatedSelections.concat(value.map((v) => v[wafflesKeyColumnName]));
          }
        }
        // if every selectable visible row is selected, uncheck them
      } else if (numberOfRenderedSelections <= this.props.selectedKeys?.length && numberOfRenderedSelections === this.props.data.filter((d) => !d.rowIsDisabled && !d.checkBoxIsDisabled).length) {
        const renderedSelectedKeys = renderedSelections.map((rs) => rs[wafflesKeyColumnName]);
        updatedSelections = this.props.selectedKeys.filter((sk) => !renderedSelectedKeys.includes(sk));
      } else {
        updatedSelections = this.props.selectedKeys.concat([...this.props.data.filter((d) => !d.rowIsDisabled && !d.checkBoxIsDisabled).map((d) => d[wafflesKeyColumnName])]);
      }

      if (!!this.props.compareData && this.props.compareData.length > 0) {
        updatedSelections = updatedSelections.concat(this.props.compareData.filter((d) => !d.rowIsDisabled && !d.checkBoxIsDisabled).map((d) => d[wafflesKeyColumnName]));
      }
    }

    uniqueKeys = [...new Set(updatedSelections)];

    this.props.handleCheckboxChange(uniqueKeys, true);
  }

  private handleCheckboxClick = (key: string, isExpansionParent?: boolean, isExpansionParentAlreadyChecked?: boolean) => {
    let updatedSelections = [...this.props.selectedKeys];
    if (isExpansionParent) {
      const expansionChildrenKeys = this.props.dataExpansions.get(key).map((child) => child[wafflesKeyColumnName]);
      if (isExpansionParentAlreadyChecked) {
        // they were all already selected, so remove all of these children
        updatedSelections = updatedSelections.filter((us) => !expansionChildrenKeys.includes(us));
      } else {
        // not all were selected, so add all of these children
        // the new Set line below dedupes them
        updatedSelections = updatedSelections.concat(expansionChildrenKeys);
      }
    } else {
      // Compare mode inherently has two of each item, so we have to remove all of them
      const selectedItems = this.props.selectedKeys.filter((sk) => {
        return sk === key;
      });

      if (selectedItems.length > 0) {
        selectedItems.forEach((selectedItem) => {
          updatedSelections.splice(updatedSelections.indexOf(selectedItem), 1);
        });
      } else {
        updatedSelections.push(key);
      }
    }
    const uniqueKeys = [...new Set(updatedSelections)];
    this.props.handleCheckboxChange(uniqueKeys);
  }

  private handleColumnResize = (event: React.MouseEvent, col: keyof T) => {
    this.hasResizedColumns = true;
    const cells = this.tableContainerRef.current.querySelectorAll(`th[data-key="${col as string}"] .waffles-head-title, td[data-key="${col as string}"]`);
    const initialTableLeft = this.tableContainerRef.current.getBoundingClientRect().left;
    const initialMouseX = event.pageX;
    const headerInitialWidth = cells[0].getBoundingClientRect().width;
    const arbitraryMinimumColumnWidth = this.arbitraryMinimumColumnWidth;

    const resizeIndicator = this.wafflesResizePositionIndicatorRef.current;
    resizeIndicator.style.left = `${initialMouseX - initialTableLeft}px`;
    resizeIndicator.style.top = this.tableContainerRef.current.querySelector(".fake-table")?.classList.contains("fix") ?
      `${Math.abs(this.tableContainerRef.current.getBoundingClientRect().top)}px` :
      `${(event.target as HTMLElement).closest("thead").scrollHeight}px`;
    resizeIndicator.style.display = "block";

    let headerCurrentWidth = headerInitialWidth;
    const moveHandler = (e: MouseEvent) => {
      headerCurrentWidth = Math.max(headerInitialWidth - (initialMouseX - e.pageX), arbitraryMinimumColumnWidth);
      resizeIndicator.style.left = `${e.pageX - initialTableLeft}px`;
    };
    window.addEventListener("mousemove", moveHandler);

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", moveHandler);
      const newColumnWidths = new Map(this.state.columnWidths);
      const newWidth = Math.max(headerCurrentWidth, arbitraryMinimumColumnWidth);
      resizeIndicator.style.display = "none";
      newColumnWidths.set(col, newWidth.toString());
      cells.forEach((item) => {
        (item as HTMLElement).style.width = `${newWidth}px`;
        (item as HTMLElement).style.maxWidth = `${newWidth}px`;
      });
      window.removeEventListener("mouseup", handleMouseUp);
      this.setState({
        columnWidths: newColumnWidths,
        containerWidth: this.tableContainerRef.current.clientWidth,
      });
    };
    window.addEventListener("mouseup", handleMouseUp);
  }

  private handleCustomSort = (column: keyof T, direction: SortDirections) => {
    if (this.props.passThroughSorting) {
      this.props.customSort(undefined, column, direction);
      const newHeaders = this.buildHeaders(this.state.usedColumns, column, direction);
      this.setState({
        fakeTableHead: newHeaders.fakeHeader,
        sortDirection: direction,
        sortedColumn: column,
        tableHead: newHeaders.thead,
      });
      return;
    }
    const records = new Map(this.state.records);
    let comparedRecords: T[] = [];
    if (direction === SortDirections.None) {
      if (this.includeFiltering && this.filters.length > 0) {
        this.handleFilterChange(this.filters, true);
      } else {
        const newRecords = this.buildRecords();
        const newHeaders = this.buildHeaders(this.state.usedColumns, column, direction);
        const newBody = this.buildTBody(this.state.usedColumns, newRecords);
        this.setState({
          fakeTableHead: newHeaders.fakeHeader,
          records: newRecords,
          sortDirection: direction,
          sortedColumn: column,
          tableHead: newHeaders.thead,
          tableRows: newBody,
        });
      }
    } else {
      // if we're showing load more, it means there are more records than we have available and should
      // just let the parent component handle all the sorting, since it has (or can get) all the information
      if (this.state.showLoadMore) {
        const sortedRecords = this.props.customSort(undefined, column, direction);
        const newRecords = this.buildRecords(sortedRecords);
        const newBody = this.buildTBody(this.state.usedColumns, newRecords);
        const newHeaders = this.buildHeaders(this.state.usedColumns, column, direction);
        this.setState({
          fakeTableHead: newHeaders.fakeHeader,
          records: newRecords,
          sortDirection: direction,
          sortedColumn: column,
          tableHead: newHeaders.thead,
          tableRows: newBody,
        });
      } else {
        const keys = [...records.keys()];
        comparedRecords = this.props.compareData;
        keys.forEach((key) => {
          let segment = records.get(key);
          if (comparedRecords) {
            segment = segment.reduce((result, value, index, array) => {
              if (index % 2 === 0) {
                if (value[wafflesKeyColumnName] === null) {
                  value[wafflesKeyColumnName] = array[index + 1][wafflesKeyColumnName];
                }
                result.push(value);
              }
              return result;
            }, []);
          }
          const rowsWithoutExpansions = segment.filter((row) => !row.isExpansion);
          const sorted = this.props.customSort(rowsWithoutExpansions, column, direction);
          records.set(key, sorted);
        });

        /* in most cases we prefer to let the parent component dictate the records,
         * but updating that prop just calls the sort function again.
         * This also has the advantage of keeping the props unsorted, in case our sortDirection is None again.
         */
        const newRecords = this.buildComparedRecords(records, comparedRecords, direction);
        const newBody = this.buildTBody(this.state.usedColumns, newRecords);
        const newHeaders = this.buildHeaders(this.state.usedColumns, column, direction);
        this.setState({
          fakeTableHead: newHeaders.fakeHeader,
          records: newRecords,
          sortDirection: direction,
          sortedColumn: column,
          tableHead: newHeaders.thead,
          tableRows: newBody,
        });
      }
    }
  }

  private handleFilterChange = async (filters: WafflesFilterGroup[], unsort?: boolean) => {
    let newRecords: Map<string, T[]>;
    this.filters = filters;
    if (this.props.passThroughFiltering) {
      const newData = this.props.passThroughFiltering(filters, this.state.sortedColumn, this.state.sortDirection);
      newRecords = this.buildRecords(
        filters.length ? newData.data : this.props.data,
        filters.length ? newData.comparedData : this.props.compareData,
      );
    } else {
      const dataClone = [...this.props.data];
      const comparedDataClone = this.props.compareData ? [...this.props.compareData] : [];
      let filteredData: T[] = [];
      let comparedFilteredData: T[] = [];

      // avoid doing a bunch of unnecessary work
      if (filters.length > 0) {
        filteredData = wafflesDefaultFiltering(dataClone, filters, this.props.onFilterChange);
        comparedFilteredData = wafflesDefaultFiltering(comparedDataClone, filters, this.props.onFilterChange);
      }

      newRecords = this.buildRecords(
        filters.length ? filteredData : this.props.data,
        filters.length ? comparedFilteredData : this.props.compareData,
      );
    }

    // Like with customSort, we generally prefer letting the parent dictate records,
    // but it's more efficient to let the parent keep an unfiltered source of truth
    const newBody = this.buildTBody(this.state.usedColumns, newRecords);
    const shouldShowLoadMore = this.props.loadMore && !this.props.enablePagination && this.props.loadMore(true);

    this.setState((prevState) => ({
      records: newRecords,
      showLoadMore: shouldShowLoadMore,
      sortDirection: unsort ? SortDirections.None : prevState.sortDirection,
      sortedColumn: prevState.sortedColumn,
      tableRows: newBody,
    }), () => {
      //this.handleCustomSort(this.state.sortedColumn, this.state.sortDirection);
      if (unsort) {
        const newHeaders = this.buildHeaders(this.state.usedColumns);
        this.setState({
          fakeTableHead: newHeaders.fakeHeader,
          tableHead: newHeaders.thead,
        });
      }
    });
  }

  // This sort function is lightweight and assumes the data is just text/numeric.
  // Sorting more complex stuff, like components, can be handled with the customSort prop.
  // See ByGeocode.tsx for an example of parsing a number from inside a string inside a Link component.
  private handleInternalSort = (column: keyof T, maintainDirection?: boolean) => {
    let records = new Map(this.state.records);
    const keys = [...records.keys()];
    const newSortDirection: SortDirections = maintainDirection ?
      this.state.sortDirection :
      this.getNewSortDirection(column);
    let newColumn = column;

    if (newSortDirection === SortDirections.None) {
      records = this.buildRecords();
      newColumn = null;
    } else {
      const directionMultiplier = newSortDirection === SortDirections.Asc ? 1 : -1;
      keys.forEach((k) => {
        const sorted = records.get(k).sort((a, b) => (a[column] > b[column] ? 1 : -1) * directionMultiplier);
        records.set(k, sorted);
      });
    }
    const newBody = this.buildTBody(this.state.usedColumns, records);
    const newHeaders = this.buildHeaders(this.state.usedColumns, newColumn, newSortDirection);
    this.setState({
      fakeTableHead: newHeaders.fakeHeader,
      records,
      sortDirection: newSortDirection,
      sortedColumn: newColumn,
      tableHead: newHeaders.thead,
      tableRows: newBody,
    });
  }

  private handleLoadMore = () => {
    const keepShowing = this.props.loadMore(false);
    this.setState({
      showLoadMore: keepShowing,
    });
  }

  private handleTableScroll = () => {
    const newScrollLeft = this.tableContainerRef.current.scrollLeft;

    if (!this.props.disableStickyHeader && !this.props.virtualize) {
      const fakeTable = this.tableContainerRef.current.querySelector(".fake-table");
      if (fakeTable) {
        fakeTable.scrollLeft = newScrollLeft;
      }
    }

    const table = this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table}`);
    if (this.scrollRightRef.current) {
      if (table && Math.ceil(newScrollLeft + this.state.containerWidth) >= Math.floor(table.getBoundingClientRect().width)) {
        this.scrollRightRef.current.classList.add("hide");
        if (this.scrollRightOverlayRef.current) {
          this.scrollRightOverlayRef.current.classList.add("hide");
        }
      } else {
        this.scrollRightRef.current.classList.remove("hide");
        if (this.scrollRightOverlayRef.current) {
          this.scrollRightOverlayRef.current.classList.remove("hide");
        }
      }
    }

    if (this.scrollLeftRef.current) {
      if (newScrollLeft <= 0) {
        this.scrollLeftRef.current.classList.add("hide");
        if (this.scrollLeftOverlayRef.current) {
          this.scrollLeftOverlayRef.current.classList.add("hide");
        }
      } else {
        this.scrollLeftRef.current.classList.remove("hide");
        if (this.scrollLeftOverlayRef.current) {
          this.scrollLeftOverlayRef.current.classList.remove("hide");
        }
      }
    }

    if (this.props.virtualize && !this.initialRender && this.tableContainerRef.current.scrollTop !== this.state.scrollTop) {
      window.clearTimeout(this.tableScrollTimeout);
      this.tableScrollTimeout = window.setTimeout(() => {
        this.setState({
          columnWidths: this.recordColumnWidths(this.state.usedColumns),
        }, () => {
          const updatedHeaders = this.buildHeaders(this.state.usedColumns);
          const updatedTable = this.buildTBody(this.state.usedColumns, this.state.records);
          this.setState({
            fakeTableHead: updatedHeaders.fakeHeader,
            tableHead: updatedHeaders.thead,
            tableRows: updatedTable,
          });
        });
      }, 300);
      this.setState({
        containerScrollLeft: newScrollLeft,
        scrollTop: this.tableContainerRef.current.scrollTop,
      });
    } else {
      this.setState({
        containerScrollLeft: newScrollLeft,
      });
    }
  }

  private handleWindowResize = () => {
    // only set state after the event has stopped for 50ms
    this.clearResizeInterval();
    this.windowResizeInterval = window.setTimeout(() => {
      const newContainerWidth = this.tableContainerRef.current ? this.tableContainerRef.current.clientWidth : null;
      const newHeaders = this.buildHeaders(this.state.usedColumns, undefined, undefined, newContainerWidth);
      this.setState({
        columnWidths: this.recordColumnWidths(this.state.usedColumns),
        containerWidth: newContainerWidth,
        fakeTableHead: newHeaders.fakeHeader,
        tableHead: newHeaders.thead,
      });
    }, 50);
  }

  private handleWindowScroll = () => {
    if (!this.props.virtualize && this.tableContainerRef.current && this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table}`)) {
      const tableHeight = this.tableContainerRef.current.getBoundingClientRect().height;
      const tableTop = this.tableContainerRef.current.getBoundingClientRect().top;
      const tableBottom = this.tableContainerRef.current.getBoundingClientRect().bottom;

      // this will run the calculations/changes 15ms after the user stops scrolling
      this.clearWindowScrollInterval();
      this.windowScrollInterval = window.setTimeout(() => {
        // 56 is the nav height
        const fakeTable = this.tableContainerRef.current.querySelector(".fake-table") as HTMLElement;
        if (!this.props.disableStickyHeader && fakeTable) {
          if (tableTop < 56 && tableBottom > fakeTable.offsetHeight + 56 && !fakeTable.classList.contains("fix")) {
            fakeTable.classList.add("fix");
          } else if ((tableTop >= 56 || tableBottom < fakeTable.offsetHeight + 56) && fakeTable.classList.contains("fix")) {
            fakeTable.classList.remove("fix");
          }
        }

        if (this.props.loadMore && this.props.infiniteScroll && !this.props.enablePagination) {
          if ((window.scrollY + window.innerHeight) > (tableTop + tableHeight * 0.8)) {
            const keepLoading = this.props.loadMore(false);
            this.setState({
              showLoadMore: keepLoading,
            });
          }
        }

        // sticky pagination bar currently doesn't work because waffles-outer-container is overflow: hidden
        // const paginationBar = this.tableContainerRef.current.parentElement.querySelector(".waffles-pagination");
        // if (paginationBar && this.props.enablePagination) {
        //   // the pagination bar is normally position sticky, but we don't want it hovering over the table header
        //   // z-index can't do it because the two aren't siblings/can't be siblings
        //   if (this.shouldStickPaginationBar()) {
        //     paginationBar.classList.add("position-static");
        //   } else {
        //     paginationBar.classList.remove("position-static");
        //   }
        // }
      }, 15);
    }
  }

  private scrollLeft = () => {
    if (this.tableContainerRef && this.tableContainerRef.current) {
      this.scrollInterval = window.setInterval(() => {
        this.tableContainerRef.current.scrollLeft = (this.tableContainerRef.current.scrollLeft - this.tableContainerRef.current.clientWidth * 0.015);
        if (this.tableContainerRef.current.scrollLeft <= 0) {
          this.clearScrollInterval();
        }
      }, 10);
    }
  }

  private scrollRight = () => {
    // The typeof window check probably isn't necessary anywhere. It's an old habit from rendering static pages, where node doesn't have a window object.
    if (this.tableContainerRef && this.tableContainerRef.current) {
      this.scrollInterval = window.setInterval(() => {
        this.tableContainerRef.current.scrollLeft = (this.tableContainerRef.current.scrollLeft + this.tableContainerRef.current.clientWidth * 0.015);
        if ((this.tableContainerRef.current.scrollLeft +
          this.tableContainerRef.current.clientWidth) >=
          this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table}`).clientWidth) {
          this.clearScrollInterval();
        }
      }, 10);
    }
  }

  /* UTILITY/HELPER METHODS */

  private clearEventListeners = () => {
    window.removeEventListener("resize", this.handleWindowResize);
    const sidePanel = this.tableContainerRef.current && this.tableContainerRef.current.closest(".side-panel");
    if (sidePanel) {
      sidePanel.removeEventListener("scroll", this.handleWindowScroll);
    } else {
      window.removeEventListener("scroll", this.handleWindowScroll);
    }
  }

  private clearResizeInterval = () => {
    window.clearInterval(this.windowResizeInterval);
  }

  private clearScrollInterval = () => {
    window.clearInterval(this.scrollInterval);
  }

  private clearWindowScrollInterval = () => {
    window.clearInterval(this.windowScrollInterval);
  }

  private getNewSortDirection = (column: keyof T) => {
    const invert = this.props.invertInitialSort;
    if (column !== this.state.sortedColumn) {
      return invert ? SortDirections.Desc : SortDirections.Asc;
    }

    switch (this.state.sortDirection) {
      case SortDirections.Asc:
        return invert ? SortDirections.None : SortDirections.Desc;
      case SortDirections.Desc:
        return invert ? SortDirections.Asc : SortDirections.None;
      case SortDirections.None:
        return invert ? SortDirections.Desc : SortDirections.Asc;
      default:
        return SortDirections.None;
    }
  }

  private getRenderedSelections = (): T[] => {
    const dataExpansionChildren: T[] = Array.from(this.props.dataExpansions || []).reduce((result, [, value]) => {
      result.push(...value.map((v) => v));
      return result;
    }, []);
    const allData = this.props.data.concat(dataExpansionChildren);
    return allData.filter((row) => this.props.selectedKeys.includes(row[wafflesKeyColumnName]));
  }

  private getThemeClass = (theme: WafflesThemes) => {
    switch (theme) {
      case WafflesThemes.Flyout:
        return "flyout";
      case WafflesThemes.Simple:
        return "simple";
      case WafflesThemes.Scrollable:
        return "scrollable";
      case WafflesThemes.AccessPointDetails:
        return "access-point-details";
      case WafflesThemes.ContractEntityDetails:
        return "contract-entity-details";
      case WafflesThemes.ComplianceConnect:
        return "compliance-connect";
      case WafflesThemes.Default:
      default:
        return "";
    }
  }

  private getUsedColumns = () => {
    if ((!this.props.data || !this.props.data[0]) && (!this.props.compareData || !this.props.compareData[0])) {
      return [];
    }
    const usedData = this.props.data[0] || this.props.compareData[0];
    let usedColumns: Array<keyof T> = [];
    const columns: Array<keyof T> = Object.keys(usedData) as Array<keyof T>;
    columns.forEach((col) => {
      const colData = this.props.columns && this.props.columns[col as string];
      if (col !== wafflesKeyColumnName &&
        ((colData && !colData.hidden) ||
          (!this.props.columns &&
            (this.props.data.some((d) => d[col] !== undefined && d[col] !== null) &&
              (!this.props.compareData || this.props.compareData.some((d) => d[col] !== undefined && d[col] !== null)))))) {
        usedColumns.push(col);
      }
    });
    if (this.props.columns && Object.values(this.props.columns).some((value) => !!value.sortOrder)) {
      usedColumns = usedColumns.sort((a, b) => {
        const av = this.props.columns[a as string].sortOrder || Infinity;
        const bv = this.props.columns[b as string].sortOrder || Infinity;
        return av > bv ? 1 : -1;
      });
    }
    return usedColumns;
  }

  // Since the callback could have any number of parameters and return a variety of types, it just has to be any
  private processChanges = (callback: () => void) => {
    this.setProcessingOverlay();

    // this is weird, but it forces the browser to render the processing overlay update
    // before running the process function, instead of waiting until after.
    // requestAnimationFrame is called immediately before the next repaint, while setTimeout is placed at the end of the event queue
    // This could also be handled with window.requestIdleCallback, but our version of TS doesn't list it as a function and it's not supported by IE or Safari
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        callback();
        this.removeProcessingOverlay();
      });
    });
  }

  private recordColumnWidths = (usedColumns: Array<keyof T>/*, initial: boolean*/) => {
    if (!this.tableContainerRef.current || !this.tableContainerRef.current.querySelector("td")) {
      return new Map<keyof T, string>();
    }
    const newColumnWidths = new Map(this.state.columnWidths);
    for (const col of usedColumns) {
      const cellSelector = `td[data-key="${col as string}"], tbody th[data-key="${col as string}"]`;
      //should get the cell with the widest width
      let cell: HTMLElement;
      if (this.props.virtualize) {
        const allCells = [...this.tableContainerRef.current.querySelectorAll(cellSelector)];
        cell = allCells.reduce((a, b) => {
          // .scrollWidth will get the width of a child text node, instead of offsetWidth's actual element width. Tested in Chrome, Firefox, and Edge Legacy. 7/13/2020
          const result = a.scrollWidth > b.scrollWidth ? a : b;
          return result;
        }) as HTMLElement;
      } else {
        cell = this.tableContainerRef.current.querySelector(`${cellSelector}:not(:empty)`) || this.tableContainerRef.current.querySelector(cellSelector);
      }
      const head: HTMLElement = this.tableContainerRef.current.querySelector(
        `.${WafflesClassNames.table} th[data-key="${col as string}"] .waffles-head-content,
         .${WafflesClassNames.table} th[data-key="${col as string}"] .waffles-head-content--tooltip`
      );

      if (head && cell) {
        const cellStyle = window.getComputedStyle(cell);
        const headStyle = window.getComputedStyle(head);

        const cellPadding = parseFloat(cellStyle.paddingLeft) + parseFloat(cellStyle.paddingRight);
        const headPadding = parseFloat(headStyle.paddingLeft) + parseFloat(headStyle.paddingRight);

        let sortArrowWidth = 0;
        if (!this.props.useVerticalHeaders) {
          const sortArrowElement = head.querySelector(".waffles-sort-indicator");

          if (sortArrowElement) {
            sortArrowWidth = parseFloat(window.getComputedStyle(sortArrowElement).width);
          }
        }

        const headWidth = parseFloat(headStyle.width);
        const oldCellWidth = parseFloat(cellStyle.width);
        const cellWidth: string = Math.max(headWidth - headPadding - sortArrowWidth, oldCellWidth - cellPadding - sortArrowWidth).toString();

        newColumnWidths.set(col, cellWidth);
      }
    }
    return newColumnWidths;
  }

  private removeProcessingOverlay = () => {
    if (this.processingOverlayRef.current && !this.props.loading) {
      this.processingOverlayRef.current.classList.remove("show");
    }
  }

  private setEventListeners = () => {
    window.addEventListener("resize", this.handleWindowResize);
    const sidePanel = this.tableContainerRef.current.closest(".side-panel");
    if (sidePanel) {
      sidePanel.addEventListener("scroll", this.handleWindowScroll);
    } else {
      window.addEventListener("scroll", this.handleWindowScroll);
    }
    // if this table is inside an accordion...
    if (this.tableContainerRef.current.closest(".qes-accordion__body-wrapper")) {
      // we need to get the width of the container once it's actually rendered. Otherwise it's 0 and that does us no good.
      const accordionObserver = new MutationObserver((e) => {
        if ((e[0].target as HTMLElement).classList.contains("qes-js-open")) {
          if ((Boolean)(this.tableContainerRef.current) && this.tableContainerRef.current.clientWidth !== this.state.containerWidth) {
            this.setState({
              columnWidths: this.recordColumnWidths(this.state.usedColumns),
              containerWidth: this.tableContainerRef.current.clientWidth,
            }, () => {
              const newBody = this.buildTBody(this.state.usedColumns, this.state.records);
              const newHeads = this.buildHeaders(this.state.usedColumns);
              this.setState({
                fakeTableHead: newHeads.fakeHeader,
                tableHead: newHeads.thead,
                tableRows: newBody,
              });
            });
          }
        }
      });

      accordionObserver.observe(this.tableContainerRef.current.closest(".qes-accordion__body-wrapper"), {
        attributeFilter: ["class"],
        attributes: true,
      });
    }
  }

  private setProcessingOverlay = () => {
    if (this.processingOverlayRef.current && !this.props.disableProcessingOverlay) {
      this.processingOverlayRef.current.classList.add("show");
    }
  }

  // sticky pagination bar currently doesn't work because waffles-outer-container is overflow: hidden
  // private shouldStickPaginationBar = () => {
  //   if (!this.tableContainerRef.current || !this.props.enablePagination) {
  //     return false;
  //   }
  //   const paginationBar = this.tableContainerRef.current.parentElement.querySelector(".waffles-pagination");
  //   const thead = this.tableContainerRef.current.querySelector(`.${WafflesClassNames.table} thead`);
  //   // if (paginationBar && thead && thead.getBoundingClientRect().bottom > window.innerHeight - paginationBar.getBoundingClientRect().height) {
  //     if (paginationBar && thead && thead.getBoundingClientRect().bottom < -100) {
  //     return true;
  //   }
  //   return false;
  // }
}

export { Waffles };
